import React, { useState, useEffect, useMemo, useRef } from 'react';
import api, { trackActivity, portfolioRegistryAPI } from '../services/api';
import ResearchReportModal from '../components/ResearchReportModal';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import XLSX from 'xlsx-js-style';
import html2canvas from 'html2canvas';
import './PortfolioViewerApp.css';
import {
  RISK_METHOD_NOTE, buildSourceLines, registrySourceMap, fyGapNotes, riskGapNotes,
} from '../utils/performanceDisclosure';
import useDisclaimer, { disclaimerStateText } from '../hooks/useDisclaimer';
import ManagedDisclaimer from '../components/ManagedDisclaimer';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Performance sub-views that share the start/end month pickers (and the Custom
// Period payload they drive).
const MONTH_RANGE_VIEWS = ['custom', 'monthly'];

// Series colours for the Monthly performance charts, in fixed assignment order
// (2026 master deck palette; ordered so neighbours stay distinguishable).
const PERF_SERIES_COLOURS = ['#012341', '#337B91', '#AA8090', '#A6C6C1', '#12706C', '#FBAE40', '#5E819C', '#BBCBD7'];
const perfSeriesColour = (i) => PERF_SERIES_COLOURS[i % PERF_SERIES_COLOURS.length];

// Color palette for asset classes - aligned with blending tool
const ASSET_CLASS_COLORS = {
  'Global Alpha': '#BBCBD7',              // Light grey-blue
  'Global Alpha - D': '#BBCBD7',          // Light grey-blue
  'Global Alpha - G': '#BBCBD7',          // Light grey-blue
  'Australian Equities': '#011E41',       // Very dark blue
  'International Equities': '#5E819C',    // Slate blue
  'Global Equities': '#5E819C',           // Slate blue
  'Global Equities - H': '#5E819C',       // Slate blue
  'Real Estate': '#A6C6C1',               // Pale teal
  'Infrastructure': '#337B91',            // Teal
  'Cash': '#EFE6D5',                      // Light beige
  'Government Bonds': '#F3BD48',          // Yellow/gold
  'Fixed Income - D': '#F3BD48',          // Yellow/gold
  'Corporate Debt': '#AA8090',            // Dusty pink
  'Private Debt': '#874564',              // Dark burgundy
  'Private Equity': '#6C757D',            // Grey
  'Real Assets (Private Market)': '#127080', // Dark teal
  'Alternatives': '#9B59B6',              // Purple
  'Emerging Markets': '#34495E',          // Dark slate
};

const getAssetClassColor = (assetClass, index) => {
  if (ASSET_CLASS_COLORS[assetClass]) {
    return ASSET_CLASS_COLORS[assetClass];
  }
  const fallbackColors = [
    '#072040', '#f0a500', '#599BE3', '#91CF9B', '#E74C3C',
    '#9B59B6', '#1ABC9C', '#E67E22', '#34495E', '#7F8C8D',
    '#2C3E50', '#27AE60', '#8E44AD', '#D35400', '#16A085'
  ];
  return fallbackColors[index % fallbackColors.length];
};

// ── SAA tilt bars — replicated from the Trading app so they look identical ──
// Trading-app asset-class colours + canonical order, plus the exact TiltBar component.
const TILT_AC_COLORS = {
  'Australian Equities': '#011E41', 'International Equities': '#5E819C', 'Global Equities': '#5E819C',
  'Real Estate': '#A6C6C1', 'Infrastructure': '#337B91', 'Cash': '#C9A227',
  'Government Bonds': '#F3BD48', 'Corporate Debt': '#AA8090', 'Private Debt': '#874564',
  'Private Equity': '#6C757D', 'Alternatives': '#9B59B6', 'Emerging Markets': '#34495E',
};
const TILT_AC_ORDER = [
  'Australian Equities', 'International Equities', 'EM Equities', 'Real Estate', 'Infrastructure',
  'Global Alpha - G', 'Global Alpha', 'Private Equity', 'Cash', 'Government Bonds', 'Corporate Debt',
  'Private Debt', 'Private Infrastructure', 'Global Alpha - D',
];
const tiltAcRank = (ac) => { const i = TILT_AC_ORDER.indexOf(ac); return i === -1 ? 999 : i; };
const tiltAcColour = (ac, i) => TILT_AC_COLORS[ac]
  || ['#072040', '#f0a500', '#599BE3', '#91CF9B', '#874564', '#127080', '#9B59B6', '#E67E22'][i % 8];

// Region colours for the regional tilt bars (dot + bar fill).
const REGION_COLORS = {
  'North America': '#011E41', 'Europe & UK': '#5E819C', 'Japan': '#337B91',
  'Asia-Ex Japan': '#A6C6C1', 'EM & Other': '#34495E', 'Cash': '#C9A227',
};
const regionColour = (r, i) => REGION_COLORS[r]
  || ['#072040', '#f0a500', '#599BE3', '#91CF9B', '#874564', '#127080', '#9B59B6', '#E67E22'][i % 8];

/* Diverging horizontal bar (target − SAA neutral): colour = asset-class colour;
   direction (right/left of centre) = over/under. Exact copy of the Trading app. */
function TiltBar({ value, max, color, suffix = '% vs SAA' }) {
  const v = Number(value) || 0;
  const w = Math.min(50, (Math.abs(v) / (max || 1)) * 50);
  const over = v >= 0;
  const near = Math.abs(v) < 0.05;
  return (
    <div className="tiltbar" title={`${v >= 0 ? '+' : ''}${v.toFixed(1)}${suffix}`}>
      <span className="tiltbar-track">
        {!near && (
          <span className="tiltbar-fill"
            style={{ background: color, ...(over ? { left: '50%', width: `${w}%` } : { right: '50%', width: `${w}%` }) }} />
        )}
      </span>
      <span className={`tiltbar-num ${near ? 'flat' : ''}`}>
        {near ? '·' : `${v >= 0 ? '+' : ''}${v.toFixed(1)}`}
      </span>
    </div>
  );
}

// Default portfolios for Drummond users (in display order)
const DRUMMOND_DEFAULT_PORTFOLIOS = [
  'DDP',       // Drummond Dynamic Portfolio W/S
  'DRU005SU',  // Yarra Lane - DS30 Super
  'DRU006SU',  // Yarra Lane - DS50 Super
  'DRU007SU',  // Yarra Lane - DS70 Super
  'DRU008SU',  // Yarra Lane - DS90 Super
  'FLPDHIG1',  // FLP Dynamic HG
  'DSP100PLUS', // Drummond Strategic 100 Plus W/S
];

// Custom sort order for portfolio columns
const getPortfolioSortOrder = (code) => {
  const order = DRUMMOND_DEFAULT_PORTFOLIOS.indexOf(code);
  return order >= 0 ? order : 999; // Non-default portfolios go to the end
};

const PortfolioViewerApp = () => {
  // General performance note: managed in Admin > Disclaimers (perf_general_note)
  const perfNote = useDisclaimer('perf_general_note');
  const { theme } = useTheme();
  const { user, loading: authLoading } = useAuth();
  // Internal Drummond users see every client's tailored series, so the "default to tailored"
  // behaviour below is scoped to branded client logins.
  const isDrummondUser = user?.email?.endsWith('@drummondcp.com') || user?.email?.endsWith('@drummond.com');

  // State
  const [openReportModals, setOpenReportModals] = useState([]);
  const [portfolios, setPortfolios] = useState([]);
  const [selectedPortfolios, setSelectedPortfolios] = useState([]);
  const [holdingsData, setHoldingsData] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [historyPortfolio, setHistoryPortfolio] = useState('');
  const [isLoadingPortfolios, setIsLoadingPortfolios] = useState(true);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [showPortfolioModal, setShowPortfolioModal] = useState(false);
  const [modalSearch, setModalSearch] = useState('');
  const [selectedSuite, setSelectedSuite] = useState(null);
  const [openDrill, setOpenDrill] = useState(null);   // blender_code of the open platform drill
  // "Select Portfolios" slicer facets (multi-select; OR within a facet, AND across facets)
  const [sliceSeries, setSliceSeries] = useState([]);
  const [slicePlatforms, setSlicePlatforms] = useState([]);
  const [sliceMasters, setSliceMasters] = useState([]);    // Master SAA (blender_code)
  const [sliceClients, setSliceClients] = useState([]);    // client_domain
  const [openFacet, setOpenFacet] = useState(null);        // which facet dropdown is open
  const [performanceData, setPerformanceData] = useState(null);
  // Performance sub-view: the Periods table plus Financial Year and Risk tables ported
  // from the Portfolio Dashboard (fetched lazily when their sub-tab is opened).
  const [perfView, setPerfView] = useState('periods');      // 'periods' | 'fy' | 'risk' | 'custom'
  // Benchmark the Performance views compare against: the assigned benchmark (registry
  // override, else the AMI peer) or, for Drummond users only, each portfolio's CPI+
  // return objective from Investment Config. The ref lets late responses for the
  // other mode be dropped.
  const [benchMode, setBenchMode] = useState('assigned');   // 'assigned' | 'objective'
  const benchModeRef = useRef('assigned');
  const [customAvail, setCustomAvail] = useState(null);     // { code: {first_month, last_month, benchmark_name} }
  const [customStart, setCustomStart] = useState('');       // 'YYYY-MM', inclusive
  const [customEnd, setCustomEnd] = useState('');           // 'YYYY-MM', inclusive
  const [customData, setCustomData] = useState(null);       // { code: {portfolio, benchmark, difference, ..._annualised, months} }
  const [loadingCustom, setLoadingCustom] = useState(false);
  const [customError, setCustomError] = useState(null);
  const [monthlyShowBench, setMonthlyShowBench] = useState(false);   // Monthly view: overlay each benchmark
  const [fyReturnsData, setFyReturnsData] = useState({});   // { code: { columns: [...] } }
  const [loadingFY, setLoadingFY] = useState(false);
  const [riskMetricsData, setRiskMetricsData] = useState(null); // { code: {...metrics} }
  const [loadingRisk, setLoadingRisk] = useState(false);
  const [tiltsData, setTiltsData] = useState(null);
  const [tiltsPortfolio, setTiltsPortfolio] = useState('');
  const [exposureData, setExposureData] = useState(null);   // growth/defensive + active/passive history
  const [exposureErr, setExposureErr] = useState(null);
  const [tiltsByCode, setTiltsByCode] = useState({});   // {code: {asset_class_group: tilt}} — per-column SAA tilt bars under the holdings table
  const [regionalByCode, setRegionalByCode] = useState({});   // {code: {data:[{region,portfolio_weight,benchmark_weight}], benchmark_name}} — regional look-through charts
  const [holdingsHistoryData, setHoldingsHistoryData] = useState(null);
  const [holdingsHistoryPortfolio, setHoldingsHistoryPortfolio] = useState('');
  const [expandedAssetClasses, setExpandedAssetClasses] = useState({});
  const [expandedMonths, setExpandedMonths] = useState({});
  const [fundsWithReports, setFundsWithReports] = useState(new Set());
  const [screenerPerfData, setScreenerPerfData] = useState({});
  const [registryData, setRegistryData] = useState(null);
  const [registryLoaded, setRegistryLoaded] = useState(false);
  // Tab state
  const [activeTab, setActiveTab] = useState('holdings');
  // Holdings table sort: which portfolio column drives the row order (null = far-right default)
  const [holdingsSortCol, setHoldingsSortCol] = useState(null);
  const [holdingsSortDir, setHoldingsSortDir] = useState('desc');

  // Stale-response guards: switching suite tabs fires new holdings/performance requests
  // while older (often larger, slower) ones are still in flight. Without these, a slow
  // earlier response can land last and overwrite the current selection's data (e.g. show
  // the previous suite's columns). Each request captures a sequence id and only applies
  // its result if it is still the latest. Single-portfolio panels guard by their code.
  const gridSeqRef = useRef(0);
  const historyReqRef = useRef(null);
  const tiltsReqRef = useRef(null);
  const exposureReqRef = useRef(null);
  const holdingsHistReqRef = useRef(null);

  // Image export targets: the off-screen clone is mounted inside the app container
  // so scoped CSS (.portfolio-viewer-container .performance-table etc.) still applies.
  const containerRef = useRef(null);
  const holdingsTableRef = useRef(null);
  const performancePanelRef = useRef(null);

  // Track app access and load portfolios and funds with reports on mount
  useEffect(() => {
    trackActivity('app_access', 'portfolio_viewer');
    loadPortfolios();
    loadFundsWithReports();
    portfolioRegistryAPI.getRegistry('portfolio_viewer')
      .then(setRegistryData).catch(() => {}).finally(() => setRegistryLoaded(true));
  }, []);

  // ─── Model-first structure: Suite → Model → Platform vehicle ─────────────
  // The registry IS the model list (one row per blender_code, auth-filtered). The flat
  // `portfolios` list is the auth-scoped platform vehicles (every code has Shadow data).
  // A suite shows its MODELS as chips (one canonical column each); platform variants are
  // an on-demand drill. Both sources are server-scoped, so nothing here can leak.

  // vehicles grouped by their model (blender_code); unmapped ones bucket separately
  const vehiclesByModel = useMemo(() => {
    const m = {};
    portfolios.forEach((p) => {
      const bc = p.blender_code || '__UNMAPPED__';
      (m[bc] = m[bc] || []).push(p);
    });
    return m;
  }, [portfolios]);
  const allowedSet = useMemo(() => new Set(portfolios.map((p) => p.portfolio_code)), [portfolios]);

  // The column a model renders as: its registry canonical vehicle if that has data, else
  // the first mapped vehicle of the model (auth-safe — only vehicles the user may see).
  const columnCodeForModel = (model) => {
    if (allowedSet.has(model.portfolio_code)) return model.portfolio_code;
    const vs = vehiclesByModel[model.blender_code];
    return vs && vs.length ? vs[0].portfolio_code : null;
  };

  // Non-tailored suites → model chips. Tailored models are grouped by their SERIES
  // (Altura / Derwent / Pathway …) and selected a whole series at a time, not per model.
  const { modelSuites, tailoredSeries } = useMemo(() => {
    const suites = registryData?.suites || [];
    if (!registryLoaded && !suites.length) return { modelSuites: [], tailoredSeries: [] };
    const groups = [];
    const tSeries = {};   // series label → model entries
    const tClients = {};  // series label → client / brand names (Drummond users only)
    suites.forEach((suite) => {
      if (suite.clients?.length) tClients[suite.label] = suite.clients;
      const models = [];
      suite.portfolios.forEach((model) => {
        const code = columnCodeForModel(model);
        if (!code) return;
        const entry = { blenderCode: model.blender_code, name: model.display_name,
                        growth: model.growth_exposure, code };
        if (model.tailored) (tSeries[suite.label] = tSeries[suite.label] || []).push(entry);
        else models.push(entry);
      });
      if (models.length) groups.push({ label: suite.label, models });
    });
    const series = Object.entries(tSeries)
      .map(([label, models]) => ({ label, models, codes: models.map((m) => m.code),
                                   clients: tClients[label] || [] }));
    return { modelSuites: groups, tailoredSeries: series };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registryData, registryLoaded, portfolios]);

  // Tab strip: one tab per non-tailored suite + a "Tailored" tab (series toggles drive its
  // selection, so the tab itself carries no codes).
  const availableTabs = useMemo(() => {
    const tabs = modelSuites.map((g) => ({ label: g.label, codes: g.models.map((m) => m.code) }));
    if (tailoredSeries.length) tabs.push({ label: 'Tailored', codes: [] });
    return tabs;
  }, [modelSuites, tailoredSeries]);
  const activeModels = useMemo(() => {
    if (selectedSuite === 'Tailored') return [];
    return (modelSuites.find((g) => g.label === selectedSuite) || modelSuites[0] || {}).models || [];
  }, [modelSuites, selectedSuite]);

  // A model is "on" if its canonical column or any of its platform vehicles is selected.
  const modelIsActive = (m) => selectedPortfolios.includes(m.code)
    || (vehiclesByModel[m.blenderCode] || []).some((v) => selectedPortfolios.includes(v.portfolio_code));

  const toggleModel = (m) => {
    const vs = (vehiclesByModel[m.blenderCode] || []).map((v) => v.portfolio_code);
    if (modelIsActive(m)) {
      setSelectedPortfolios((prev) => prev.filter((c) => c !== m.code && !vs.includes(c)));
    } else {
      setSelectedPortfolios((prev) => [...new Set([...prev, m.code])]);
    }
  };

  // Tailored series toggle: select/deselect a whole series' models at once.
  const seriesIsActive = (s) => s.codes.length > 0 && s.codes.some((c) => selectedPortfolios.includes(c));
  const toggleSeries = (s) => {
    if (seriesIsActive(s)) {
      setSelectedPortfolios((prev) => prev.filter((c) => !s.codes.includes(c)));
    } else {
      setSelectedPortfolios((prev) => [...new Set([...prev, ...s.codes])]);
    }
  };

  // Platform drill: add/remove a specific platform vehicle of a model as its own column
  const toggleVehicle = (code) => setSelectedPortfolios((prev) =>
    prev.includes(code) ? prev.filter((c) => c !== code) : [...new Set([...prev, code])]);

  // Choose the initial tab + selection once data is in. A branded client with tailored
  // portfolios lands on the Tailored tab with those selected (their own portfolios); everyone
  // else lands on the first suite with its models selected.
  useEffect(() => {
    // Wait for auth to resolve so the branded/internal distinction is known before deciding.
    if (!authLoading && availableTabs.length > 0 && selectedSuite === null) {
      if (!isDrummondUser && tailoredSeries.length > 0) {
        setSelectedSuite('Tailored');
        setSelectedPortfolios(tailoredSeries.flatMap((s) => s.codes));
      } else {
        setSelectedSuite(availableTabs[0].label);
        setSelectedPortfolios(availableTabs[0].codes);
      }
    }
  }, [authLoading, availableTabs, selectedSuite, tailoredSeries, isDrummondUser]);

  // Load data when portfolios change
  useEffect(() => {
    if (selectedPortfolios.length > 0) {
      const seq = ++gridSeqRef.current;   // newest selection wins, stale responses ignored
      loadHoldingsData(seq);
      loadPerformanceData(seq);
      // Set history/tilts portfolio to first selected if not already set or if current is deselected
      if (!historyPortfolio || !selectedPortfolios.includes(historyPortfolio)) {
        setHistoryPortfolio(selectedPortfolios[0]);
      }
      if (!tiltsPortfolio || !selectedPortfolios.includes(tiltsPortfolio)) {
        setTiltsPortfolio(selectedPortfolios[0]);
      }
      if (!holdingsHistoryPortfolio || !selectedPortfolios.includes(holdingsHistoryPortfolio)) {
        setHoldingsHistoryPortfolio(selectedPortfolios[0]);
      }
    } else {
      setHoldingsData(null);
      setHistoryData(null);
      setPerformanceData(null);
      setTiltsData(null);
      setHoldingsHistoryData(null);
      setHistoryPortfolio('');
      setTiltsPortfolio('');
      setHoldingsHistoryPortfolio('');
    }
  }, [selectedPortfolios]);

  // Load history when historyPortfolio changes
  useEffect(() => {
    if (historyPortfolio) {
      loadHistoryData(historyPortfolio);
    } else {
      setHistoryData(null);
    }
  }, [historyPortfolio]);

  // Load tilts when tiltsPortfolio changes
  useEffect(() => {
    if (tiltsPortfolio) {
      loadTiltsData(tiltsPortfolio);
    } else {
      setTiltsData(null);
    }
  }, [tiltsPortfolio]);

  // Growth/defensive + active/passive history for the SAA Tilts tab — fetched only while
  // that tab is open (it reads the AP List as well as Shadow_Data).
  useEffect(() => {
    if (activeTab !== 'tilts' || !tiltsPortfolio) return;
    const code = tiltsPortfolio;
    exposureReqRef.current = code;
    setExposureData(null); setExposureErr(null);
    api.get(`/api/portfolio/portfolios/${code}/exposure-history`)
      .then((r) => { if (exposureReqRef.current === code) setExposureData(r.data); })
      .catch((e) => { if (exposureReqRef.current === code) setExposureErr(e.response?.data?.detail || e.message); });
  }, [activeTab, tiltsPortfolio]);

  // Load holdings history when holdingsHistoryPortfolio changes
  useEffect(() => {
    if (holdingsHistoryPortfolio) {
      loadHoldingsHistoryData(holdingsHistoryPortfolio);
    } else {
      setHoldingsHistoryData(null);
    }
  }, [holdingsHistoryPortfolio]);

  // SAA tilts (target − neutral, by asset class group) for EVERY selected portfolio,
  // so the holdings table can show per-column tilt bars at the bottom (mirrors the
  // Trading app). Uses the /tilts endpoint's `latest` snapshot per portfolio.
  useEffect(() => {
    if (!isDrummondUser || !selectedPortfolios.length) { setTiltsByCode({}); return; }
    let cancelled = false;
    Promise.all(selectedPortfolios.map((code) =>
      api.get(`/api/portfolio/portfolios/${code}/tilts`)
        .then((r) => ({ code, latest: r.data?.latest || {} }))
        .catch(() => ({ code, latest: {} }))
    )).then((results) => {
      if (cancelled) return;
      const m = {};
      results.forEach(({ code, latest }) => { m[code] = latest; });
      setTiltsByCode(m);
    });
    return () => { cancelled = true; };
  }, [selectedPortfolios, isDrummondUser]);

  // Regional look-through exposure (latest available, portfolio vs MSCI ACWI) for each
  // selected portfolio — charts below the holdings table (Drummond users only).
  useEffect(() => {
    if (!isDrummondUser || !selectedPortfolios.length) { setRegionalByCode({}); return; }
    let cancelled = false;
    Promise.all(selectedPortfolios.map((code) =>
      api.get(`/api/portfolio-reports/portfolio/${code}/regional-lookthrough`)
        .then((r) => ({ code, data: r.data }))
        .catch(() => ({ code, data: null }))
    )).then((results) => {
      if (cancelled) return;
      const m = {};
      results.forEach(({ code, data }) => { if (data?.data?.length) m[code] = data; });
      setRegionalByCode(m);
    });
    return () => { cancelled = true; };
  }, [selectedPortfolios, isDrummondUser]);

  const loadPortfolios = async () => {
    try {
      const response = await api.get('/api/portfolio/portfolios', { params: { include_approved_targets: true } });
      setPortfolios(response.data);
    } catch (error) {
      console.error('Error loading portfolios:', error);
    } finally {
      setIsLoadingPortfolios(false);
    }
  };

  const loadFundsWithReports = async () => {
    try {
      const response = await api.get('/api/portfolio/funds-with-reports');
      setFundsWithReports(new Set(response.data.apirs || []));
    } catch (error) {
      console.error('Error loading funds with reports:', error);
    }
  };

  const loadHoldingsData = async (seq) => {
    setIsLoadingData(true);
    try {
      const codes = selectedPortfolios.join(',');
      const response = await api.get(`/api/portfolio/portfolios/holdings?portfolio_codes=${codes}`);
      if (seq === gridSeqRef.current) setHoldingsData(response.data);
    } catch (error) {
      console.error('Error loading holdings:', error);
    } finally {
      if (seq === gridSeqRef.current) setIsLoadingData(false);
    }
  };

  const loadHistoryData = async (portfolioCode) => {
    historyReqRef.current = portfolioCode;
    try {
      const response = await api.get(`/api/portfolio/portfolios/${portfolioCode}/history`);
      if (historyReqRef.current === portfolioCode) setHistoryData(response.data);
    } catch (error) {
      console.error('Error loading history:', error);
    }
  };

  const loadPerformanceData = async (seq) => {
    const mode = benchModeRef.current;
    try {
      const codes = selectedPortfolios.join(',');
      const response = await api.get(`/api/portfolio/portfolios/performance?portfolio_codes=${codes}&hide_flagged_performance=true&benchmark=${mode}`);
      if (seq === gridSeqRef.current && mode === benchModeRef.current) setPerformanceData(response.data);
    } catch (error) {
      console.error('Error loading performance:', error);
    }
  };

  const loadTiltsData = async (portfolioCode) => {
    tiltsReqRef.current = portfolioCode;
    try {
      const response = await api.get(`/api/portfolio/portfolios/${portfolioCode}/tilts`);
      if (tiltsReqRef.current === portfolioCode) setTiltsData(response.data);
    } catch (error) {
      console.error('Error loading tilts data:', error);
      if (tiltsReqRef.current === portfolioCode) setTiltsData(null);
    }
  };

  const loadHoldingsHistoryData = async (portfolioCode) => {
    holdingsHistReqRef.current = portfolioCode;
    try {
      const response = await api.get(`/api/portfolio/portfolios/${portfolioCode}/holdings-history`);
      if (holdingsHistReqRef.current !== portfolioCode) return;
      setHoldingsHistoryData(response.data);
      // Reset expansion state when loading new data
      setExpandedAssetClasses({});
      setExpandedMonths({});
    } catch (error) {
      console.error('Error loading holdings history:', error);
      if (holdingsHistReqRef.current === portfolioCode) setHoldingsHistoryData(null);
    }
  };

  // Load screener performance data when holdings load
  useEffect(() => {
    if (holdingsData?.holdings?.length > 0) {
      const trCodes = [...new Set(holdingsData.holdings.map(h => h.tr_code).filter(Boolean))];
      if (trCodes.length > 0) {
        api.post('/api/screener/funds-by-tr-codes', { tr_codes: trCodes })
          .then(response => {
            setScreenerPerfData(response.data.funds || {});
          })
          .catch(error => {
            console.error('Error loading screener performance data:', error);
            setScreenerPerfData({});
          });
      }
    } else {
      setScreenerPerfData({});
    }
  }, [holdingsData]);

  // Helper function for formatting performance data
  const formatPercentDirect = (value) => {
    if (value === null || value === undefined) return '-';
    return `${value.toFixed(1)}%`;
  };

  // Per-fund returns as-at month (e.g. 'Jul-26') — lagged PM funds sit at an earlier
  // month-end than the rest of the table, so the date is shown per row.
  const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const formatAsAtMonth = (d) => {
    if (!d) return '-';
    const [y, m] = String(d).split('-');
    return (y && m) ? `${MONTHS_SHORT[+m - 1]}-${y.slice(2)}` : d;
  };

  // Performance columns config
  const perfColumns = [
    { key: 'mtd', label: 'MTD', format: formatPercentDirect },
    { key: 'return_1m', label: '1m', format: formatPercentDirect },
    { key: 'return_3m', label: '3m', format: formatPercentDirect },
    { key: 'return_6m', label: '6m', format: formatPercentDirect },
    { key: 'return_12m', label: '12m', format: formatPercentDirect },
    { key: 'return_3yr', label: '3yr', format: formatPercentDirect },
    { key: 'return_5yr', label: '5yr', format: formatPercentDirect },
  ];

  // Toggle asset class expansion
  const toggleAssetClassExpansion = (assetClass) => {
    setExpandedAssetClasses(prev => ({
      ...prev,
      [assetClass]: !prev[assetClass]
    }));
  };

  // Toggle month expansion
  const toggleMonthExpansion = (monthLabel) => {
    setExpandedMonths(prev => ({
      ...prev,
      [monthLabel]: !prev[monthLabel]
    }));
  };

  // Handle portfolio selection
  const handlePortfolioToggle = (code) => {
    setSelectedPortfolios(prev => {
      if (prev.includes(code)) {
        return prev.filter(c => c !== code);
      } else {
        return [...prev, code];
      }
    });
  };

  // ─── Slicer facet metadata (keyed by the vehicle's model / blender_code) ──────
  // blender_code → { series (suite label), masterName } from the registry.
  const modelFacetByBlender = useMemo(() => {
    const m = {};
    (registryData?.suites || []).forEach((s) => s.portfolios.forEach((p) => {
      if (p.blender_code) m[p.blender_code] = { series: s.label, masterName: p.display_name || p.blender_code };
    }));
    return m;
  }, [registryData]);
  const seriesOf = (p) => modelFacetByBlender[p.blender_code]?.series || (p.blender_code ? 'Other' : 'Unmapped');
  const masterOf = (p) => p.blender_code || null;                  // Master SAA
  const clientOf = (p) => p.client_domain || null;                // branded client

  // Facet options as {value,label}; only values present among the user's allowed vehicles.
  const seriesOptions = useMemo(() => {
    const order = {};
    (registryData?.suites || []).forEach((s, i) => { order[s.label] = i; });
    return [...new Set(portfolios.map(seriesOf))]
      .sort((a, b) => (order[a] ?? 900) - (order[b] ?? 900) || a.localeCompare(b))
      .map((v) => ({ value: v, label: v }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolios, registryData, modelFacetByBlender]);
  const platformOptions = useMemo(
    () => [...new Set(portfolios.map((p) => p.platform || 'Unknown'))].sort().map((v) => ({ value: v, label: v })),
    [portfolios]
  );
  const masterOptions = useMemo(() => {
    const seen = new Map();   // blender_code → label
    portfolios.forEach((p) => {
      if (p.blender_code && !seen.has(p.blender_code)) {
        seen.set(p.blender_code, modelFacetByBlender[p.blender_code]?.masterName || p.blender_code);
      }
    });
    return [...seen.entries()].map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [portfolios, modelFacetByBlender]);
  const clientOptions = useMemo(() => {
    const seen = new Map();   // client_domain → label
    portfolios.forEach((p) => {
      if (p.client_domain && !seen.has(p.client_domain)) {
        seen.set(p.client_domain, p.client_name || p.client_domain);
      }
    });
    return [...seen.entries()].map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [portfolios]);

  // Slicer result: vehicles passing search + all active facets (OR within a facet, AND
  // across facets), grouped by series. This is what the modal list shows.
  const modalGroups = useMemo(() => {
    const q = modalSearch.trim().toLowerCase();
    const match = (p) => !q
      || (p.portfolio_name || '').toLowerCase().includes(q)
      || (p.display_name || '').toLowerCase().includes(q)
      || (p.portfolio_code || '').toLowerCase().includes(q)
      || (p.platform || '').toLowerCase().includes(q)
      || (p.client_name || '').toLowerCase().includes(q);
    const passes = (p) => match(p)
      && (!sliceSeries.length || sliceSeries.includes(seriesOf(p)))
      && (!slicePlatforms.length || slicePlatforms.includes(p.platform || 'Unknown'))
      && (!sliceMasters.length || sliceMasters.includes(masterOf(p)))
      && (!sliceClients.length || sliceClients.includes(clientOf(p)));
    const order = {};
    (registryData?.suites || []).forEach((s, i) => { order[s.label] = i; });
    const rank = (label) => label === 'Other' ? 901 : label === 'Unmapped' ? 902 : (order[label] ?? 500);
    const bucket = {};
    portfolios.filter(passes).forEach((p) => { (bucket[seriesOf(p)] = bucket[seriesOf(p)] || []).push(p); });
    return Object.entries(bucket)
      .sort(([a], [b]) => rank(a) - rank(b))
      .map(([label, items]) => ({
        label,
        portfolios: items.sort((a, b) =>
          (a.display_name || a.portfolio_name).localeCompare(b.display_name || b.portfolio_name)),
      }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolios, modalSearch, sliceSeries, slicePlatforms, sliceMasters, sliceClients, registryData, modelFacetByBlender]);

  // All portfolio codes currently visible in the modal (for "Select Visible")
  const modalVisibleCodes = useMemo(
    () => modalGroups.flatMap((g) => g.portfolios.map((p) => p.portfolio_code)),
    [modalGroups]
  );
  const toggleFacet = (setter) => (value) => setter((prev) =>
    prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]);
  const clearSlices = () => { setSliceSeries([]); setSlicePlatforms([]); setSliceMasters([]); setSliceClients([]); };

  // Helper to get display name for a portfolio code
  const getDisplayName = (code) => {
    const p = portfolios.find(port => port.portfolio_code === code);
    return p?.display_name || p?.portfolio_name || code;
  };

  // ─── Build per-suite disclaimer groups from registry ──────────────────
  // Source / fee wording comes from each portfolio's registry `performance_source`
  // (Investment Config), so it follows the platform the returns actually come from.
  // Two formats live there: full statements that carry their own fee basis (often
  // self-labelled, e.g. "Compass ETF (50/70): Performance is before ...") and older
  // fragments ("They are calculated from the model portfolio within ...") that
  // continue the standard fee sentence, which is prefixed only for those.
  const suiteDisclaimers = useMemo(() => {
    if (!registryData?.suites) return [];
    const codeToSuite = {};
    const codeToInception = {};
    const codeToSource = registrySourceMap(registryData);
    registryData.suites.forEach((suite) => {
      suite.portfolios.forEach((p) => {
        codeToSuite[p.portfolio_code] = suite.label;
        if (p.inception_date) codeToInception[p.portfolio_code] = p.inception_date;
      });
    });

    // Only portfolios whose performance is actually shown (same rule as the Periods
    // table: a non-zero Inception return) — a hidden row needs no disclosure.
    const shown = new Set((performanceData?.portfolios || [])
      .filter((p) => { const inc = p.performance?.['Inception']?.portfolio; return inc != null && inc !== 0; })
      .map((p) => p.portfolio_code));
    const suiteMap = {};
    selectedPortfolios.filter((code) => shown.has(code)).forEach((code) => {
      const suite = codeToSuite[code] || null;   // null = platform vehicle outside the registry
      const key = suite || '__vehicles__';
      (suiteMap[key] = suiteMap[key] || []).push(code);
    });

    return Object.entries(suiteMap).map(([key, codes]) => {
      if (key === '__vehicles__') {
        // Drill-down platform vehicles have no registry disclosure of their own.
        const lines = codes.map((code) => {
          const p = portfolios.find((x) => x.portfolio_code === code);
          return `${getDisplayName(code)}: returns for the ${p?.platform || 'platform'} vehicle as reported by that platform; the fee basis may differ from the model disclosures above.`;
        });
        return { suite: 'Platform vehicles', inceptionText: '', sourceLines: lines };
      }

      // Inception text for this suite
      const dateGroups = {};
      codes.forEach((code) => {
        const raw = codeToInception[code];
        if (!raw) return;
        const d = new Date(raw);
        const formatted = `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
        (dateGroups[formatted] = dateGroups[formatted] || []).push(getDisplayName(code));
      });
      const entries = Object.entries(dateGroups);
      let inceptionText = '';
      if (entries.length === 1) {
        inceptionText = ` Inception date is ${entries[0][0]}.`;
      } else if (entries.length > 1) {
        inceptionText = ' ' + entries.map(([date, names]) =>
          `Inception date for ${names.join(' & ')} is ${date}.`
        ).join(' ');
      }

      const sourceLines = buildSourceLines(codes, codeToSource, getDisplayName);
      return { suite: key, inceptionText, sourceLines };
    });
  }, [registryData, selectedPortfolios, portfolios, performanceData]);

  // Export holdings, performance and holdings history to Excel
  const exportToExcel = async () => {
    const wb = XLSX.utils.book_new();

    // Style definitions
    const headerStyle = {
      font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 10 },
      fill: { fgColor: { rgb: '072040' } },
      alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
      border: {
        bottom: { style: 'thin', color: { rgb: '003366' } },
      },
    };
    const acStyle = {
      font: { bold: true, sz: 10, color: { rgb: '072040' } },
      fill: { fgColor: { rgb: 'E7E6E6' } },
      border: {
        bottom: { style: 'thin', color: { rgb: 'CCCCCC' } },
        top: { style: 'thin', color: { rgb: 'CCCCCC' } },
      },
    };
    const acNumStyle = { ...acStyle, alignment: { horizontal: 'center' }, numFmt: '0.0"%"' };
    const cellStyle = {
      font: { sz: 10 },
      border: { bottom: { style: 'hair', color: { rgb: 'DDDDDD' } } },
    };
    const numStyle = { ...cellStyle, alignment: { horizontal: 'center' }, numFmt: '0.0"%"' };
    const totalStyle = {
      font: { bold: true, sz: 10, color: { rgb: '072040' } },
      fill: { fgColor: { rgb: 'F0A500' } },
      border: { top: { style: 'medium', color: { rgb: '072040' } }, bottom: { style: 'medium', color: { rgb: '072040' } } },
    };
    const totalNumStyle = { ...totalStyle, alignment: { horizontal: 'center' }, numFmt: '0.0"%"' };
    const metricStyle = {
      font: { bold: true, sz: 10 },
      fill: { fgColor: { rgb: 'F5F5F5' } },
      border: { bottom: { style: 'hair', color: { rgb: 'DDDDDD' } } },
    };
    const metricNumStyle = { ...metricStyle, alignment: { horizontal: 'center' }, numFmt: '0.00"%"' };

    // Helper: apply styles to a sheet, set column widths
    const styleSheet = (ws, colCount, colWidths) => {
      ws['!cols'] = colWidths.map(w => ({ wch: w }));
      // Style header row
      for (let c = 0; c < colCount; c++) {
        const ref = XLSX.utils.encode_cell({ r: 0, c });
        if (ws[ref]) ws[ref].s = headerStyle;
      }
    };

    // --- Sheet 1: Holdings ---
    if (holdingsData?.holdings?.length > 0) {
      const sortedPorts = [...(holdingsData.portfolios || [])].sort((a, b) =>
        getPortfolioSortOrder(a.code) - getPortfolioSortOrder(b.code)
      );

      const headers = ['Fund Name', 'APIR', ...sortedPorts.map(p => getDisplayName(p.code))];
      const aoa = [headers];
      const rowMeta = []; // track row types for styling

      const groups = {};
      holdingsData.holdings.forEach(h => {
        const group = h.asset_class_group || 'Other';
        if (!groups[group]) groups[group] = [];
        groups[group].push(h);
      });

      Object.entries(groups).forEach(([assetClass, holdings]) => {
        // Asset class header row
        const acRow = [assetClass, '', ...sortedPorts.map(p => {
          let total = 0;
          holdings.forEach(h => { total += (h.weights[p.code] || 0); });
          return total;
        })];
        aoa.push(acRow);
        rowMeta.push('ac');

        holdings.forEach(h => {
          aoa.push([
            h.asset_name,
            h.apir_code || '',
            ...sortedPorts.map(p => h.weights[p.code] ?? ''),
          ]);
          rowMeta.push('data');
        });
      });

      // Totals
      const totalRow = ['Total', '', ...sortedPorts.map(p => {
        let total = 0;
        holdingsData.holdings.forEach(h => { total += (h.weights[p.code] || 0); });
        return total;
      })];
      aoa.push(totalRow);
      rowMeta.push('total');

      // Summary metrics
      if (holdingsData.summary) {
        [
          ['FX Exposure', 'fx_exposure'],
          ['Growth Exposure', 'growth_exposure'],
          ['Defensive Exposure', 'defensive_exposure'],
          ...(isDrummondUser ? [['Fees & Costs (wtd)', 'fees_costs_wtd'], ['Net (wtd)', 'net_wtd']] : []),
        ].forEach(([label, metric]) => {
          aoa.push([label, '', ...sortedPorts.map(p => holdingsData.summary[p.code]?.[metric] ?? '')]);
          rowMeta.push('metric');
        });
      }

      const ws = XLSX.utils.aoa_to_sheet(aoa);
      const colWidths = [35, 12, ...sortedPorts.map(() => 18)];
      styleSheet(ws, headers.length, colWidths);

      // Style data rows
      for (let r = 0; r < rowMeta.length; r++) {
        const excelRow = r + 1; // +1 for header
        const type = rowMeta[r];
        for (let c = 0; c < headers.length; c++) {
          const ref = XLSX.utils.encode_cell({ r: excelRow, c });
          if (!ws[ref]) ws[ref] = { v: '', t: 's' };
          if (type === 'ac') ws[ref].s = c >= 2 ? acNumStyle : acStyle;
          else if (type === 'total') ws[ref].s = c >= 2 ? totalNumStyle : totalStyle;
          else if (type === 'metric') ws[ref].s = c >= 2 ? metricNumStyle : metricStyle;
          else ws[ref].s = c >= 2 ? numStyle : cellStyle;
        }
      }

      XLSX.utils.book_append_sheet(wb, ws, 'Holdings');
    }

    // --- Sheet 2: Performance ---
    if (performanceData?.portfolios?.length > 0) {
      const sortedPerf = [...performanceData.portfolios]
        .filter(p => {
          const inc = p.performance?.['Inception']?.portfolio;
          return inc != null && inc !== 0;
        })
        .sort((a, b) => getPortfolioSortOrder(a.portfolio_code) - getPortfolioSortOrder(b.portfolio_code));
      const periods = performanceData.periods || [];

      // Context travels with the numbers: benchmark name and as-at date per row, plus
      // the same disclosure notes as the screen underneath the table.
      const headers = ['Portfolio', 'Benchmark', 'As at', 'Metric', ...periods];
      const NUM_LABEL_COLS = 4;
      const aoa = [headers];
      const rowMeta = [];
      const fmtAsAtCell = (iso) => {
        if (!iso) return '';
        const d = new Date(`${String(iso).slice(0, 10)}T00:00:00`);
        return isNaN(d) ? String(iso) : d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
      };

      const diffPosStyle = {
        font: { sz: 10, color: { rgb: '006100' } },
        fill: { fgColor: { rgb: 'C6EFCE' } },
        alignment: { horizontal: 'center' },
        numFmt: '0.0"%"',
      };
      const diffNegStyle = {
        font: { sz: 10, color: { rgb: '9C0006' } },
        fill: { fgColor: { rgb: 'FFC7CE' } },
        alignment: { horizontal: 'center' },
        numFmt: '0.0"%"',
      };
      const perfPortStyle = {
        font: { bold: true, sz: 10 },
        alignment: { horizontal: 'center' },
        numFmt: '0.0"%"',
        border: { bottom: { style: 'hair', color: { rgb: 'DDDDDD' } } },
      };
      const perfAmiStyle = {
        font: { sz: 10, color: { rgb: '666666' } },
        alignment: { horizontal: 'center' },
        numFmt: '0.0"%"',
        border: { bottom: { style: 'hair', color: { rgb: 'DDDDDD' } } },
      };

      sortedPerf.forEach(portfolio => {
        const name = getDisplayName(portfolio.portfolio_code);
        const bench = portfolio.ami_benchmark || '';
        const asAt = fmtAsAtCell(portfolio.as_at_date);
        const ctx = [name, bench, asAt];
        aoa.push([...ctx, 'Portfolio', ...periods.map(p => portfolio.performance[p]?.portfolio ?? '')]);
        rowMeta.push('portfolio');
        aoa.push([...ctx, 'Benchmark', ...periods.map(p => portfolio.performance[p]?.ami ?? '')]);
        rowMeta.push('ami');
        aoa.push([...ctx, 'Difference', ...periods.map(p => portfolio.performance[p]?.difference ?? '')]);
        rowMeta.push('diff');
        aoa.push(Array(headers.length).fill(''));
        rowMeta.push('blank');
      });

      // Disclosure notes (same wording as on screen) + export stamp
      const noteLines = [
        disclaimerStateText(perfNote, 'perf_general_note'),
        ...suiteDisclaimers.map((sd) => `${sd.suite}:${sd.inceptionText} ${sd.sourceLines.join(' ')}`),
        `Exported ${new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}.`,
      ];
      const notesStart = aoa.length;
      noteLines.forEach((line) => aoa.push([line, ...Array(headers.length - 1).fill('')]));

      const ws = XLSX.utils.aoa_to_sheet(aoa);
      const colWidths = [30, 22, 12, 12, ...periods.map(() => 12)];
      styleSheet(ws, headers.length, colWidths);
      // Notes: one merged, wrapped cell per line across the table width
      ws['!merges'] = (ws['!merges'] || []).concat(noteLines.map((_, i) => ({
        s: { r: notesStart + i, c: 0 }, e: { r: notesStart + i, c: headers.length - 1 },
      })));
      ws['!rows'] = ws['!rows'] || [];
      noteLines.forEach((line, i) => {
        const ref = XLSX.utils.encode_cell({ r: notesStart + i, c: 0 });
        ws[ref].s = { font: { sz: 9, italic: i > 0 && i === noteLines.length - 1, color: { rgb: '3F4A59' } },
                      alignment: { wrapText: true, vertical: 'top' } };
        ws['!rows'][notesStart + i] = { hpt: Math.max(15, Math.ceil(line.length / 150) * 13) };
      });

      for (let r = 0; r < rowMeta.length; r++) {
        const excelRow = r + 1;
        const type = rowMeta[r];
        for (let c = 0; c < headers.length; c++) {
          const ref = XLSX.utils.encode_cell({ r: excelRow, c });
          if (!ws[ref]) ws[ref] = { v: '', t: 's' };
          if (type === 'blank') continue;
          if (c < NUM_LABEL_COLS) {
            // Name and metric columns
            if (type === 'portfolio') ws[ref].s = { font: { bold: true, sz: 10 }, border: { bottom: { style: 'hair', color: { rgb: 'DDDDDD' } } } };
            else if (type === 'ami') ws[ref].s = { font: { sz: 10, color: { rgb: '666666' } }, border: { bottom: { style: 'hair', color: { rgb: 'DDDDDD' } } } };
            else if (type === 'diff') ws[ref].s = { font: { bold: true, sz: 10 }, fill: { fgColor: { rgb: 'F5F5F5' } }, border: { bottom: { style: 'medium', color: { rgb: 'CCCCCC' } } } };
          } else {
            // Number columns
            if (type === 'portfolio') ws[ref].s = perfPortStyle;
            else if (type === 'ami') ws[ref].s = perfAmiStyle;
            else if (type === 'diff') {
              const val = ws[ref].v;
              ws[ref].s = (typeof val === 'number' && val >= 0) ? diffPosStyle : diffNegStyle;
            }
          }
        }
      }

      XLSX.utils.book_append_sheet(wb, ws, 'Performance');
    }

    // --- Sheet 3: Holdings History ---
    if (holdingsHistoryData?.asset_classes?.length > 0) {
      const allDates = [];
      holdingsHistoryData.months.forEach(month => {
        month.dates.forEach(date => allDates.push(date));
      });

      const formatDate = (d) => {
        const dt = new Date(d);
        return dt.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: '2-digit' });
      };

      const headers = ['Holding', ...allDates.map(formatDate)];
      const aoa = [headers];
      const rowMeta = [];

      holdingsHistoryData.asset_classes.forEach(ac => {
        aoa.push([ac.name, ...allDates.map(d => ac.totals[d] ?? '')]);
        rowMeta.push('ac');

        ac.holdings.forEach(h => {
          aoa.push([`  ${h.asset_name} (${h.apir_code || ''})`, ...allDates.map(d => h.values[d] ?? '')]);
          rowMeta.push('data');
        });
      });

      const ws = XLSX.utils.aoa_to_sheet(aoa);
      const colWidths = [40, ...allDates.map(() => 12)];
      styleSheet(ws, headers.length, colWidths);

      for (let r = 0; r < rowMeta.length; r++) {
        const excelRow = r + 1;
        const type = rowMeta[r];
        for (let c = 0; c < headers.length; c++) {
          const ref = XLSX.utils.encode_cell({ r: excelRow, c });
          if (!ws[ref]) ws[ref] = { v: '', t: 's' };
          if (type === 'ac') ws[ref].s = c >= 1 ? acNumStyle : acStyle;
          else ws[ref].s = c >= 1 ? numStyle : cellStyle;
        }
      }

      XLSX.utils.book_append_sheet(wb, ws, 'Holdings History');
    }

    // --- Sheet 4 (Drummond only): monthly series over each portfolio's full history ---
    // Same payload as the Monthly view, fetched here for the whole available history
    // (not the on-screen window) under the current benchmark mode. Month return,
    // cumulative, rolling 12-month and drawdown for portfolio and benchmark. A failed
    // fetch is written into the sheet rather than silently omitted.
    if (isDrummondUser && perfOrderedCodes.length > 0) {
      const codesKey = perfOrderedCodes.join(',');
      const params = { portfolio_codes: codesKey, hide_flagged_performance: true, benchmark: benchMode };
      const MONTHLY_METRICS = [
        ['Return', 'portfolio'], ['Benchmark', 'benchmark'], ['+/-', 'difference'], ['Cum alpha', 'cum_alpha'],
        ['Cumulative', 'portfolio_cum'], ['Bmk cumulative', 'benchmark_cum'],
        ['Rolling 12m', 'portfolio_12m'], ['Bmk rolling 12m', 'benchmark_12m'],
        ['Drawdown', 'portfolio_dd'], ['Bmk drawdown', 'benchmark_dd'],
      ];
      const monthRange = (a, b) => {
        const toIdx = (ym) => { const [y, m] = ym.split('-').map(Number); return y * 12 + m - 1; };
        const out = [];
        for (let i = toIdx(a); i <= toIdx(b); i++) out.push(`${Math.floor(i / 12)}-${String((i % 12) + 1).padStart(2, '0')}`);
        return out;
      };
      try {
        const avail = (await api.get('/api/portfolio/portfolios/custom-period', { params })).data?.portfolios || {};
        const spans = Object.values(avail).filter((a) => a?.first_month && a?.last_month);
        if (spans.length > 0) {
          const start = spans.map((a) => a.first_month).sort()[0];
          const end = spans.map((a) => a.last_month).sort().slice(-1)[0];
          const full = (await api.get('/api/portfolio/portfolios/custom-period',
            { params: { ...params, start_month: start, end_month: end } })).data?.portfolios || {};
          const codes = perfOrderedCodes.filter((c) =>
            Array.isArray(full[c]?.series) && full[c].series.some((r) => r.portfolio != null));
          if (codes.length > 0) {
            const byCode = {};
            codes.forEach((c) => {
              byCode[c] = {};
              let run = 0;   // cumulative alpha: running sum of monthly excess returns (null at a gap month)
              full[c].series.forEach((r) => {
                if (r.difference != null) run += r.difference;
                byCode[c][r.month] = { ...r, cum_alpha: r.difference == null ? null : run };
              });
            });
            const months = monthRange(start, end);
            const W = MONTHLY_METRICS.length;
            const groupHeader = ['Month', ...codes.flatMap((c) => {
              const bench = full[c].benchmark_name || codeToBenchmark[c] || 'Benchmark';
              return [`${getDisplayName(c)} (vs ${bench})`, ...Array(W - 1).fill('')];
            })];
            const subHeader = ['', ...codes.flatMap(() => MONTHLY_METRICS.map(([label]) => label))];
            const aoa = [groupHeader, subHeader];
            const gapFlags = [];   // [row][col] true where the cell is an n/r marker
            months.forEach((m) => {
              const flags = [false];
              const row = [fmtMonth(m), ...codes.flatMap((c) => {
                const r = byCode[c][m];
                return MONTHLY_METRICS.map(([, key]) => {
                  if (!r) { flags.push(false); return ''; }
                  if (r.gap && (key === 'portfolio' || key === 'difference')) { flags.push(true); return 'n/r'; }
                  flags.push(false);
                  return r[key] ?? '';
                });
              })];
              aoa.push(row);
              gapFlags.push(flags);
            });
            const noteLines = [
              `Monthly series ${fmtMonth(start)} to ${fmtMonth(end)} from the stored monthly returns, ` +
              `${benchMode === 'objective' ? 'against each portfolio\'s CPI+ return objective' : 'against each portfolio\'s assigned benchmark'}. ` +
              'Cumulative compounds from the first month shown for that portfolio. Rolling 12m needs 12 reported months. ' +
              'Drawdown is the fall from the highest cumulative level reached (0% at a new high). ' +
              'n/r: no return reported for that month; the stored series carries it at 0%, so cumulative and drawdown figures through it are indicative only. ' +
              'Drummond internal use only.',
              `Exported ${new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}.`,
            ];
            const notesStart = aoa.length;
            noteLines.forEach((line) => aoa.push([line, ...Array(groupHeader.length - 1).fill('')]));

            const ws = XLSX.utils.aoa_to_sheet(aoa);
            ws['!cols'] = [{ wch: 11 }, ...codes.flatMap(() => MONTHLY_METRICS.map(() => ({ wch: 13 })))];
            ws['!merges'] = codes.map((_, i) => ({ s: { r: 0, c: 1 + i * W }, e: { r: 0, c: W + i * W } }))
              .concat(noteLines.map((_, i) => ({ s: { r: notesStart + i, c: 0 }, e: { r: notesStart + i, c: groupHeader.length - 1 } })));
            const subHeaderStyle = { ...headerStyle, fill: { fgColor: { rgb: '5E819C' } } };
            for (let c = 0; c < groupHeader.length; c++) {
              const h0 = XLSX.utils.encode_cell({ r: 0, c });
              const h1 = XLSX.utils.encode_cell({ r: 1, c });
              if (!ws[h0]) ws[h0] = { v: '', t: 's' };
              if (!ws[h1]) ws[h1] = { v: '', t: 's' };
              ws[h0].s = headerStyle;
              ws[h1].s = subHeaderStyle;
            }
            const monthCellStyle = { ...cellStyle, font: { bold: true, sz: 10 } };
            const num2Style = { ...cellStyle, alignment: { horizontal: 'center' }, numFmt: '0.00"%"' };
            const gapCellStyle = { ...cellStyle, alignment: { horizontal: 'center' }, font: { sz: 10, italic: true, color: { rgb: '7F7F7F' } } };
            months.forEach((_, i) => {
              const r = i + 2;
              for (let c = 0; c < groupHeader.length; c++) {
                const ref = XLSX.utils.encode_cell({ r, c });
                if (!ws[ref]) ws[ref] = { v: '', t: 's' };
                ws[ref].s = c === 0 ? monthCellStyle : (gapFlags[i][c] ? gapCellStyle : num2Style);
              }
            });
            ws['!rows'] = ws['!rows'] || [];
            noteLines.forEach((line, i) => {
              const ref = XLSX.utils.encode_cell({ r: notesStart + i, c: 0 });
              ws[ref].s = { font: { sz: 9, italic: i === noteLines.length - 1, color: { rgb: '3F4A59' } },
                            alignment: { wrapText: true, vertical: 'top' } };
              ws['!rows'][notesStart + i] = { hpt: Math.max(15, Math.ceil(line.length / 150) * 13) };
            });
            XLSX.utils.book_append_sheet(wb, ws, 'Monthly');
          }
        }
      } catch (error) {
        console.error('Error building Monthly sheet:', error);
        const ws = XLSX.utils.aoa_to_sheet([[`Monthly series failed: ${error.response?.data?.detail || error.message}`]]);
        ws['!cols'] = [{ wch: 100 }];
        XLSX.utils.book_append_sheet(wb, ws, 'Monthly');
      }
    }

    XLSX.writeFile(wb, `${exportFileLabel()}_Export.xlsx`);
  };

  // Filename label from the actual selection: a single portfolio → its display name;
  // all selected portfolios in one series → that series label (ETF_Series etc.);
  // a cross-series selection → generic 'Portfolios'.
  const exportFileLabel = () => {
    if (selectedPortfolios.length === 1) {
      return getDisplayName(selectedPortfolios[0]).replace(/[^a-zA-Z0-9]/g, '_');
    }
    const suites = new Set(
      selectedPortfolios
        .map((code) => {
          const p = portfolios.find((pp) => pp.portfolio_code === code);
          return p ? seriesOf(p) : null;
        })
        .filter(Boolean)
    );
    if (suites.size === 1) return [...suites][0].replace(/[^a-zA-Z0-9]/g, '_');
    return 'Portfolios';
  };

  // Render a (transformed) clone of a node off-screen and download it as a PNG.
  // Width defaults to max-content, which suits a lone table; pass an explicit width
  // when the clone contains wrapping text (disclaimers) so paragraphs break normally.
  const exportNodeAsImage = async (node, filename, { width, prepare } = {}) => {
    if (!node) return;
    const host = document.createElement('div');
    host.style.position = 'absolute';
    host.style.left = '-10000px';
    host.style.top = '0';
    host.style.background = '#ffffff';
    host.style.padding = '16px';
    host.style.width = width ? `${width + 32}px` : 'max-content';
    const clone = node.cloneNode(true);
    if (prepare) prepare(clone);
    host.appendChild(clone);
    (containerRef.current || document.body).appendChild(host);
    try {
      const canvas = await html2canvas(host, { backgroundColor: '#ffffff', scale: 2 });
      const link = document.createElement('a');
      link.download = filename;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } finally {
      host.remove();
    }
  };

  // Holdings table as an image, without the fund performance and report columns
  const exportHoldingsImage = () => exportNodeAsImage(
    holdingsTableRef.current,
    `${exportFileLabel()}_Holdings.png`,
    {
      prepare: (clone) => {
        clone.querySelectorAll('th.perf-col, td.perf-col, th.report-col, td.report-col, th.asat-col, td.asat-col')
          .forEach((el) => el.remove());
        // Label cells spanned Fund Name + APIR + as-at + perf columns + report; now just the first two
        clone.querySelectorAll('td[colspan="11"]').forEach((td) => { td.colSpan = 2; });
      },
    }
  );

  // Performance tab (as-at line, table and disclaimers) as an image
  const exportPerformanceImage = () => {
    const panel = performancePanelRef.current;
    const table = panel?.querySelector('.performance-table');
    exportNodeAsImage(panel, `${exportFileLabel()}_Performance.png`, {
      width: table ? table.scrollWidth : undefined,
      prepare: (clone) => {
        // The Periods / Financial Year / Risk sub-view toggle is a UI control, not part
        // of the printed table — drop it from the exported image.
        clone.querySelectorAll('.perf-view-toggle').forEach((el) => el.remove());
        clone.querySelectorAll('.table-container').forEach((el) => {
          el.style.maxHeight = 'none';
          el.style.overflow = 'visible';
        });
      },
    });
  };

  // Sort portfolios in the specified order
  const sortedHoldingsPortfolios = useMemo(() => {
    if (!holdingsData?.portfolios) return [];
    return [...holdingsData.portfolios].sort((a, b) => {
      return getPortfolioSortOrder(a.code) - getPortfolioSortOrder(b.code);
    });
  }, [holdingsData]);

  // Sort performance data portfolios in the specified order
  // Filter out portfolios where Inception performance is 0.0 (no real data)
  const sortedPerformancePortfolios = useMemo(() => {
    if (!performanceData?.portfolios) return [];
    return [...performanceData.portfolios]
      .filter(p => {
        // Check if Inception performance exists and is not 0.0
        const inceptionPerf = p.performance?.['Inception']?.portfolio;
        return inceptionPerf != null && inceptionPerf !== 0;
      })
      .sort((a, b) => {
        return getPortfolioSortOrder(a.portfolio_code) - getPortfolioSortOrder(b.portfolio_code);
      });
  }, [performanceData]);

  // Do the shown portfolios all share one data date? When they don't (a mixed
  // selection where some portfolios lag), a single "As at" header would mislabel
  // the older ones, so we fall back to a per-portfolio date under each name.
  const perfDatesUniform = useMemo(() => {
    const dates = new Set(sortedPerformancePortfolios.map(p => p.as_at_date).filter(Boolean));
    return dates.size <= 1;
  }, [sortedPerformancePortfolios]);

  // ── Performance sub-views: Financial Year & Risk (mirrors the Portfolio Dashboard) ──
  // Benchmark display name per code, from the main performance payload — the same source
  // the Periods table labels its benchmark ("vs …") row with.
  const codeToBenchmark = useMemo(() => {
    const map = {};
    (performanceData?.portfolios || []).forEach((pf) => {
      if (pf.ami_benchmark) map[pf.portfolio_code] = pf.ami_benchmark;
    });
    return map;
  }, [performanceData]);

  // Selected codes in the same display order the Periods table uses.
  const perfOrderedCodes = useMemo(
    () => [...selectedPortfolios].sort((a, b) => getPortfolioSortOrder(a) - getPortfolioSortOrder(b)),
    [selectedPortfolios]
  );

  // Lazily fetch financial-year returns when that sub-view is first opened.
  useEffect(() => {
    if (activeTab !== 'performance' || perfView !== 'fy' || perfOrderedCodes.length === 0) return;
    const uncached = perfOrderedCodes.filter((c) => !fyReturnsData[c]);
    if (uncached.length === 0) return;
    setLoadingFY(true);
    const mode = benchModeRef.current;
    const request = mode === 'objective'
      ? api.get('/api/portfolio/portfolios/saa-summary',
          { params: { portfolio_codes: uncached.join(','), benchmark: mode } })
          .then((r) => uncached.map((code) => ({ code, fy: r.data?.portfolios?.[code]?.fy_returns || null })))
          .catch(() => uncached.map((code) => ({ code, fy: null })))
      : Promise.all(uncached.map((code) =>
          api.get(`/api/factsheets/portfolio/${code}`)
            .then((r) => ({ code, fy: r.data?.data?.fy_returns || null }))
            .catch(() => ({ code, fy: null }))
        ));
    request.then((results) => {
      if (mode !== benchModeRef.current) return;   // superseded by a benchmark switch
      const map = {};
      results.forEach(({ code, fy }) => { if (fy) map[code] = fy; });
      setFyReturnsData((prev) => ({ ...prev, ...map }));
      setLoadingFY(false);
    });
  }, [activeTab, perfView, perfOrderedCodes, fyReturnsData]);

  // Lazily fetch risk metrics when that sub-view is first opened.
  useEffect(() => {
    if (activeTab !== 'performance' || perfView !== 'risk' || perfOrderedCodes.length === 0) return;
    const uncached = perfOrderedCodes.filter((c) => !riskMetricsData?.[c]);
    if (uncached.length === 0) return;
    setLoadingRisk(true);
    const mode = benchModeRef.current;
    api.get('/api/portfolio/portfolios/risk-metrics',
      { params: { portfolio_codes: uncached.join(','), hide_flagged_performance: true, benchmark: mode } })
      .then((r) => {
        if (mode === benchModeRef.current) setRiskMetricsData((prev) => ({ ...prev, ...(r.data?.portfolios || {}) }));
      })
      .catch(() => {})
      .finally(() => setLoadingRisk(false));
  }, [activeTab, perfView, perfOrderedCodes, riskMetricsData]);

  // Benchmark switch: refetch the Periods payload and drop the FY / Risk caches, whose
  // figures are benchmark-specific (Custom Period refetches through its own deps).
  useEffect(() => {
    benchModeRef.current = benchMode;
    setFyReturnsData({});
    setRiskMetricsData(null);
    if (selectedPortfolios.length > 0) loadPerformanceData(gridSeqRef.current);
  }, [benchMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Benchmark label for the FY / Risk rows: in objective mode a portfolio with no
  // resolvable CPI+ objective says so rather than borrowing a peer name.
  const benchLabelFor = (code, fallback) => (benchMode === 'objective'
    ? (codeToBenchmark[code] || 'no CPI+ objective set')
    : (codeToBenchmark[code] || fallback));

  // ── Custom Period view: portfolio vs benchmark over a chosen start→end month range ──
  // Availability first (per-portfolio first/last month) to build the dropdowns; the
  // default range is the full history. Re-fetched whenever the selection changes.
  // The Monthly view (Drummond only) shares the pickers and the payload: its `series`
  // rows are the month-by-month returns inside the same window.
  const perfCodesKey = perfOrderedCodes.join(',');
  useEffect(() => {
    if (activeTab !== 'performance' || !MONTH_RANGE_VIEWS.includes(perfView) || !perfCodesKey) return;
    let cancelled = false;
    setCustomError(null);
    api.get('/api/portfolio/portfolios/custom-period',
      { params: { portfolio_codes: perfCodesKey, hide_flagged_performance: true, benchmark: benchMode } })
      .then((r) => { if (!cancelled) setCustomAvail(r.data?.portfolios || {}); })
      .catch((e) => { if (!cancelled) setCustomError(e.response?.data?.detail || e.message); });
    return () => { cancelled = true; };
  }, [activeTab, perfView, perfCodesKey, benchMode]);

  // Every month from the earliest first_month to the latest last_month across the selection.
  const customMonths = useMemo(() => {
    const avail = Object.values(customAvail || {}).filter((a) => a.first_month && a.last_month);
    if (avail.length === 0) return [];
    const toIdx = (ym) => { const [y, m] = ym.split('-').map(Number); return y * 12 + m - 1; };
    const lo = Math.min(...avail.map((a) => toIdx(a.first_month)));
    const hi = Math.max(...avail.map((a) => toIdx(a.last_month)));
    const out = [];
    for (let i = lo; i <= hi; i++) out.push(`${Math.floor(i / 12)}-${String((i % 12) + 1).padStart(2, '0')}`);
    return out;
  }, [customAvail]);

  // Default (or repair) the range when the available months change: full history,
  // so the view opens matching the Periods table before the user narrows it.
  useEffect(() => {
    if (customMonths.length === 0) return;
    const end = customMonths.includes(customEnd) ? customEnd : customMonths[customMonths.length - 1];
    const start = customMonths.includes(customStart) && customStart <= end ? customStart : customMonths[0];
    if (end !== customEnd) setCustomEnd(end);
    if (start !== customStart) setCustomStart(start);
  }, [customMonths]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab !== 'performance' || !MONTH_RANGE_VIEWS.includes(perfView) || !perfCodesKey || !customStart || !customEnd) return;
    if (customStart > customEnd) return;
    let cancelled = false;
    setLoadingCustom(true); setCustomError(null);
    api.get('/api/portfolio/portfolios/custom-period', {
      params: { portfolio_codes: perfCodesKey, start_month: customStart, end_month: customEnd, hide_flagged_performance: true, benchmark: benchMode },
    })
      .then((r) => {
        if (cancelled) return;
        setCustomData(r.data?.portfolios || {});
        setCustomPeriods(r.data?.periods || []);
      })
      .catch((e) => { if (!cancelled) setCustomError(e.response?.data?.detail || e.message); })
      .finally(() => { if (!cancelled) setLoadingCustom(false); });
    return () => { cancelled = true; };
  }, [activeTab, perfView, perfCodesKey, customStart, customEnd, benchMode]);

  const fmtMonth = (ym) => {
    if (!ym) return '';
    const [y, m] = ym.split('-').map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString('en-AU', { month: 'short', year: 'numeric' });
  };
  const [customPeriods, setCustomPeriods] = useState([]);

  // Shared by the Periods and Custom Period views: one row block per portfolio
  // (Portfolio / AMI / Difference) across the period columns. noteFor(p) returns an
  // optional line under the portfolio name (per-portfolio as-at, data coverage).
  const renderPeriodTable = (rows, periods, noteFor) => (
    <div className="table-container">
      <table className="performance-table">
        <thead>
          <tr>
            <th className="portfolio-col">Portfolio</th>
            <th className="metric-col">Metric</th>
            {periods.map(period => (
              <th key={period} className="period-col">{period}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((portfolio, idx) => {
            const note = noteFor ? noteFor(portfolio) : null;
            return (
              <React.Fragment key={portfolio.portfolio_code}>
                <tr className={`perf-row portfolio-row ${idx > 0 ? 'new-portfolio' : ''}`}>
                  <td rowSpan={3} className="portfolio-col">
                    <div className="portfolio-info">
                      <span className="portfolio-name">{getDisplayName(portfolio.portfolio_code)}</span>
                      {note && <span className="portfolio-as-at">{note}</span>}
                      <span className="ami-name">vs {portfolio.objective_missing ? 'no CPI+ objective set' : (portfolio.ami_benchmark || 'N/A')}</span>
                    </div>
                  </td>
                  <td className="metric-col">Portfolio</td>
                  {periods.map(period => (
                    <td key={period} className="period-col">
                      {portfolio.performance[period]?.portfolio != null
                        ? `${portfolio.performance[period].portfolio.toFixed(1)}%`
                        : '-'}
                    </td>
                  ))}
                </tr>
                <tr className="perf-row ami-row">
                  <td className="metric-col">{benchMode === 'objective' ? 'Objective' : 'AMI'}</td>
                  {periods.map(period => (
                    <td key={period} className="period-col">
                      {portfolio.performance[period]?.ami != null
                        ? `${portfolio.performance[period].ami.toFixed(1)}%`
                        : '-'}
                    </td>
                  ))}
                </tr>
                <tr className="perf-row diff-row">
                  <td className="metric-col">Difference</td>
                  {periods.map(period => {
                    const diff = portfolio.performance[period]?.difference;
                    return (
                      <td
                        key={period}
                        className={`period-col diff-value ${diff != null ? (diff >= 0 ? 'positive' : 'negative') : ''}`}
                      >
                        {diff != null
                          ? `${diff >= 0 ? '+' : ''}${diff.toFixed(1)}%`
                          : '-'}
                      </td>
                    );
                  })}
                </tr>
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  // Portfolios with a series at all (hidden-performance / no-data codes come back empty).
  const customVisibleCodes = useMemo(
    () => perfOrderedCodes.filter((code) => customData?.[code]?.first_month),
    [perfOrderedCodes, customData]);
  const customMonthsCount = customStart && customEnd
    ? customMonths.indexOf(customEnd) - customMonths.indexOf(customStart) + 1 : 0;

  // ── Monthly view (Drummond only): cumulative line, month-on-month bars and table ──
  // Built from the Custom Period payload's `series` (one row per month in the window,
  // with the month's return and the cumulative return since the window start, for
  // portfolio and benchmark). Gap months arrive flagged, never filled.
  const monthlyLabels = useMemo(() => {
    if (!customStart || !customEnd || customMonths.length === 0) return [];
    const i0 = customMonths.indexOf(customStart);
    const i1 = customMonths.indexOf(customEnd);
    return i0 >= 0 && i1 >= i0 ? customMonths.slice(i0, i1 + 1) : [];
  }, [customMonths, customStart, customEnd]);

  const monthlyByCode = useMemo(() => {
    const out = {};
    customVisibleCodes.forEach((code) => {
      const rows = customData?.[code]?.series;
      if (!Array.isArray(rows) || rows.length === 0) return;
      const byMonth = {};
      rows.forEach((r) => { byMonth[r.month] = r; });
      out[code] = byMonth;
    });
    return out;
  }, [customVisibleCodes, customData]);

  // Codes with at least one month return in the window, in the Periods table order.
  const monthlyCodes = useMemo(
    () => customVisibleCodes.filter((code) => monthlyLabels.some((m) => monthlyByCode[code]?.[m]?.portfolio != null)),
    [customVisibleCodes, monthlyByCode, monthlyLabels]);

  // Month before the window, so every cumulative line leaves a 0% origin (only for
  // portfolios whose history covers the window start; a later-inception portfolio
  // starts where its own data does).
  const monthlyOriginLabel = useMemo(() => {
    if (!customStart) return '';
    const [y, m] = customStart.split('-').map(Number);
    return `${m === 1 ? y - 1 : y}-${String(m === 1 ? 12 : m - 1).padStart(2, '0')}`;
  }, [customStart]);

  // One line per portfolio for a per-month field, plus a dashed benchmark line per
  // portfolio when toggled. withOrigin prepends a 0% point the month before the window
  // for portfolios whose history covers the window start. derive(row) replaces the
  // field lookup for figures computed from two fields (relative return).
  const buildMonthlyLines = (key, benchKey, { withOrigin = false, derive = null } = {}) => {
    if (monthlyLabels.length === 0 || monthlyCodes.length === 0) return null;
    const datasets = [];
    monthlyCodes.forEach((code, i) => {
      const by = monthlyByCode[code];
      const colour = perfSeriesColour(i);
      const coversStart = by[monthlyLabels[0]]?.portfolio_cum != null;
      const value = (m, k) => (derive ? derive(by[m], code) : (by[m]?.[k] ?? null));
      const line = (k, dashed) => ({
        label: dashed ? `${getDisplayName(code)} benchmark` : getDisplayName(code),
        data: [...(withOrigin ? [coversStart ? 0 : null] : []), ...monthlyLabels.map((m) => value(m, k))],
        borderColor: colour,
        backgroundColor: colour,
        borderWidth: dashed ? 1.5 : 2,
        borderDash: dashed ? [5, 4] : undefined,
        // Straight segments between month-end points (no curve smoothing, which
        // overshoots between observations); small markers show the actual months.
        pointRadius: dashed ? 0 : 2,
        pointHoverRadius: 4,
        pointHitRadius: 8,
        tension: 0,
        spanGaps: false,
      });
      datasets.push(line(key, false));
      if (monthlyShowBench && benchKey) datasets.push(line(benchKey, true));
    });
    const labels = [...(withOrigin ? [monthlyOriginLabel] : []), ...monthlyLabels].map(fmtMonth);
    return { labels, datasets };
  };
  const monthlyDeps = [monthlyLabels, monthlyCodes, monthlyByCode, monthlyShowBench, monthlyOriginLabel];
  /* eslint-disable react-hooks/exhaustive-deps */
  const monthlyLineData = useMemo(() => buildMonthlyLines('portfolio_cum', 'benchmark_cum', { withOrigin: true }), monthlyDeps);
  const monthlyDrawdownData = useMemo(() => buildMonthlyLines('portfolio_dd', 'benchmark_dd', { withOrigin: true }), monthlyDeps);
  const monthlyRollingData = useMemo(() => buildMonthlyLines('portfolio_12m', 'benchmark_12m'), monthlyDeps);
  // Cumulative return relative to the benchmark: (1 + portfolio cum) / (1 + benchmark cum) - 1.
  const monthlyRelativeData = useMemo(() => buildMonthlyLines(null, null, {
    withOrigin: true,
    derive: (r) => (r?.portfolio_cum != null && r?.benchmark_cum != null
      ? ((1 + r.portfolio_cum / 100) / (1 + r.benchmark_cum / 100) - 1) * 100 : null),
  }), monthlyDeps);
  // Cumulative alpha: running sum of the monthly excess returns (portfolio minus
  // benchmark) through the window. A gap month has no excess return, so the line
  // breaks there and resumes from the same running total.
  const monthlyAlphaByCode = useMemo(() => {
    const out = {};
    monthlyCodes.forEach((code) => {
      const by = monthlyByCode[code];
      let run = 0; let started = false;
      out[code] = {};
      monthlyLabels.forEach((m) => {
        const d = by[m]?.difference;
        if (d == null) { out[code][m] = null; return; }
        run += d; started = true;
        out[code][m] = started ? run : null;
      });
    });
    return out;
  }, monthlyDeps);
  const monthlyAlphaData = useMemo(() => buildMonthlyLines(null, null, {
    withOrigin: true,
    derive: (r, code) => (r ? (monthlyAlphaByCode[code]?.[r.month] ?? null) : null),
  }), [...monthlyDeps, monthlyAlphaByCode]);
  /* eslint-enable react-hooks/exhaustive-deps */
  const hasAnyPoint = (d) => !!d && d.datasets.some((ds) => ds.data.some((v) => v != null));

  const monthlyBarData = useMemo(() => {
    if (monthlyLabels.length === 0 || monthlyCodes.length === 0) return null;
    const datasets = [];
    monthlyCodes.forEach((code, i) => {
      const by = monthlyByCode[code];
      const colour = perfSeriesColour(i);
      datasets.push({
        label: getDisplayName(code),
        data: monthlyLabels.map((m) => by[m]?.portfolio ?? null),
        backgroundColor: colour,
        borderColor: colour,
        borderWidth: 0,
        borderRadius: 2,
        borderSkipped: false,
      });
      if (monthlyShowBench) datasets.push({
        label: `${getDisplayName(code)} benchmark`,
        data: monthlyLabels.map((m) => by[m]?.benchmark ?? null),
        backgroundColor: `${colour}66`,
        borderColor: colour,
        borderWidth: 1,
        borderRadius: 2,
        borderSkipped: false,
      });
    });
    return { labels: monthlyLabels.map(fmtMonth), datasets };
  }, [monthlyLabels, monthlyCodes, monthlyByCode, monthlyShowBench]); // eslint-disable-line react-hooks/exhaustive-deps

  const monthlyChartOptions = (yTitle, yExtra = {}) => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { position: 'bottom', labels: { usePointStyle: true, padding: 12, font: { size: 10 }, boxWidth: 8 } },
      title: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => (ctx.parsed.y == null ? null
            : `${ctx.dataset.label}: ${ctx.parsed.y >= 0 ? '+' : ''}${ctx.parsed.y.toFixed(2)}%`),
        },
      },
    },
    scales: {
      x: { grid: { display: false }, ticks: { maxTicksLimit: 12, maxRotation: 0, font: { size: 10 } } },
      y: {
        grid: { color: '#f0f0f0' },
        ticks: { callback: (v) => `${Number(v).toFixed(1)}%`, font: { size: 10 } },
        title: { display: true, text: yTitle, font: { size: 11 } },
        ...yExtra,
      },
    },
  });

  // Gap months inside the window, per portfolio, for the Monthly footnote.
  const monthlyGapNotes = useMemo(() => monthlyCodes.flatMap((code) => {
    const gaps = monthlyLabels.filter((m) => monthlyByCode[code]?.[m]?.gap);
    return gaps.length ? [`${getDisplayName(code)}: no return reported for ${gaps.map(fmtMonth).join(', ')}`] : [];
  }), [monthlyCodes, monthlyLabels, monthlyByCode]); // eslint-disable-line react-hooks/exhaustive-deps

  const fmtPct = (v, signed = false) => (v == null ? '-' : `${signed && v > 0 ? '+' : ''}${v.toFixed(2)}%`);
  const diffClass = (v) => (v == null ? '' : v >= 0 ? 'positive' : 'negative');

  // Start / end month pickers shared by the Custom Period and Monthly views.
  const customRangePicker = (
    <div className="perf-custom-range">
      <label>Start month
        <select value={customStart} onChange={(e) => setCustomStart(e.target.value)} disabled={!customMonths.length}>
          {customMonths.filter((m) => !customEnd || m <= customEnd).map((m) => (
            <option key={m} value={m}>{fmtMonth(m)}</option>
          ))}
        </select>
      </label>
      <label>End month
        <select value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} disabled={!customMonths.length}>
          {customMonths.filter((m) => !customStart || m >= customStart).map((m) => (
            <option key={m} value={m}>{fmtMonth(m)}</option>
          ))}
        </select>
      </label>
      {customMonthsCount > 0 && (
        <span className="perf-custom-span">
          1 {fmtMonth(customStart)} to end {fmtMonth(customEnd)} · {customMonthsCount} month{customMonthsCount === 1 ? '' : 's'}
        </span>
      )}
    </div>
  );

  // Unified FY columns (superset across the selected portfolios, ordered by year).
  const fyColumns = useMemo(() => {
    if (Object.keys(fyReturnsData).length === 0) return [];
    const keySet = new Map();
    perfOrderedCodes.forEach((code) => {
      (fyReturnsData[code]?.columns || []).forEach((col) => {
        if (!keySet.has(col.key)) keySet.set(col.key, col);
      });
    });
    return Array.from(keySet.values()).sort((a, b) => {
      const numA = parseInt(a.key.replace(/\D/g, ''), 10) || 0;
      const numB = parseInt(b.key.replace(/\D/g, ''), 10) || 0;
      return numA - numB;
    });
  }, [fyReturnsData, perfOrderedCodes]);

  // Codes with usable FY data: at least one non-null, non-zero portfolio return. An
  // all-zero (or all-null) portfolio has no real data, so it's hidden rather than shown
  // as a wall of 0.0% — same suppression as the Periods table.
  const fyVisibleCodes = useMemo(() => perfOrderedCodes.filter((code) => {
    const cols = fyReturnsData[code]?.columns;
    return Array.isArray(cols) && cols.some((c) => c.portfolio != null && Number(c.portfolio) !== 0);
  }), [perfOrderedCodes, fyReturnsData]);

  // Codes with usable risk metrics: at least one non-null, non-zero portfolio metric.
  const riskVisibleCodes = useMemo(() => perfOrderedCodes.filter((code) => {
    const d = riskMetricsData?.[code];
    if (!d || Object.keys(d).length === 0) return false;
    return ['tracking_error', 'volatility', 'return_risk_ratio', 'max_drawdown']
      .some((k) => d[k] != null && Number(d[k]) !== 0);
  }), [perfOrderedCodes, riskMetricsData]);

  // The portfolio column that drives the holdings row order. Defaults to the FAR-RIGHT
  // column; a header click overrides it (falls back to far-right if that column vanishes).
  const holdingsSortColEffective = useMemo(() => {
    const codes = sortedHoldingsPortfolios.map((p) => p.code);
    if (holdingsSortCol && codes.includes(holdingsSortCol)) return holdingsSortCol;
    return codes.length ? codes[codes.length - 1] : null;
  }, [holdingsSortCol, sortedHoldingsPortfolios]);

  // Group holdings by asset class, sorted by weight in the chosen column
  const groupedHoldings = useMemo(() => {
    if (!holdingsData?.holdings) return {};

    const groups = {};
    holdingsData.holdings.forEach(holding => {
      const group = holding.asset_class_group || 'Other';
      if (!groups[group]) {
        groups[group] = [];
      }
      groups[group].push(holding);
    });

    // Sort each group by weight in the chosen column (default far-right, descending)
    if (holdingsSortColEffective) {
      Object.values(groups).forEach(holdings => {
        holdings.sort((a, b) => {
          const wA = a.weights[holdingsSortColEffective] || 0;
          const wB = b.weights[holdingsSortColEffective] || 0;
          return holdingsSortDir === 'asc' ? wA - wB : wB - wA;
        });
      });
    }

    return groups;
  }, [holdingsData, holdingsSortColEffective, holdingsSortDir]);

  // Calculate totals per portfolio
  const portfolioTotals = useMemo(() => {
    if (!holdingsData?.holdings || !holdingsData?.portfolios) return {};

    const totals = {};
    holdingsData.portfolios.forEach(p => {
      totals[p.code] = 0;
    });

    holdingsData.holdings.forEach(holding => {
      Object.entries(holding.weights).forEach(([code, weight]) => {
        if (totals[code] !== undefined) {
          totals[code] += weight;
        }
      });
    });

    return totals;
  }, [holdingsData]);

  // Calculate asset class totals per portfolio
  const assetClassTotals = useMemo(() => {
    if (!holdingsData?.holdings || !holdingsData?.portfolios) return {};

    const totals = {};

    holdingsData.holdings.forEach(holding => {
      const group = holding.asset_class_group || 'Other';
      if (!totals[group]) {
        totals[group] = {};
        holdingsData.portfolios.forEach(p => {
          totals[group][p.code] = 0;
        });
      }
      Object.entries(holding.weights).forEach(([code, weight]) => {
        if (totals[group][code] !== undefined) {
          totals[group][code] += weight;
        }
      });
    });

    return totals;
  }, [holdingsData]);

  // Asset-class-group tilt rows for the holdings-table footer: the union of groups
  // that carry a tilt in any selected column, ordered like the holdings groups, with a
  // shared bar scale so columns are comparable.
  const tiltGroups = useMemo(() => {
    const codes = sortedHoldingsPortfolios.map((p) => p.code);
    const present = new Set();
    let max = 2;
    codes.forEach((c) => Object.entries(tiltsByCode[c] || {}).forEach(([acg, v]) => {
      if (Math.abs(v) > 0.05) present.add(acg);
      if (Math.abs(v) > max) max = Math.abs(v);
    }));
    // Canonical asset-class order (matches the Trading app's tilt block).
    const items = [...present].sort((a, b) => (tiltAcRank(a) - tiltAcRank(b)) || a.localeCompare(b));
    return { items, max };
  }, [tiltsByCode, sortedHoldingsPortfolios]);

  // Regional tilts (portfolio − benchmark weight per region) keyed by portfolio, in the
  // order returned, with a shared bar scale — rendered as tilt rows below the SAA tilts.
  const regionalTilts = useMemo(() => {
    const byCodeRegion = {};
    const regions = [];
    const seen = new Set();
    let max = 2;
    sortedHoldingsPortfolios.forEach((p) => {
      const rd = regionalByCode[p.code];
      if (!rd?.data) return;
      byCodeRegion[p.code] = {};
      rd.data.forEach((d) => {
        const t = Number(((d.portfolio_weight ?? 0) - (d.benchmark_weight ?? 0)).toFixed(1));
        byCodeRegion[p.code][d.region] = t;
        if (!seen.has(d.region)) { seen.add(d.region); regions.push(d.region); }
        if (Math.abs(t) > max) max = Math.abs(t);
      });
    });
    return { byCodeRegion, regions, max, has: Object.keys(byCodeRegion).length > 0 };
  }, [regionalByCode, sortedHoldingsPortfolios]);

  // Prepare chart data
  const chartData = historyData ? {
    labels: historyData.dates,
    datasets: Object.entries(historyData.series).map(([assetClass, values], index) => ({
      label: assetClass,
      data: values,
      borderColor: getAssetClassColor(assetClass, index),
      backgroundColor: getAssetClassColor(assetClass, index) + '20',
      fill: true,
      tension: 0.3,
      pointRadius: 0,
      borderWidth: 2,
    })),
  } : null;

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          usePointStyle: true,
          padding: 15,
          font: { size: 11 },
        },
      },
      title: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context) => `${context.dataset.label}: ${context.parsed.y.toFixed(1)}%`,
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          maxTicksLimit: 12,
          font: { size: 10 },
        },
      },
      y: {
        stacked: true,
        min: 0,
        max: 100,
        grid: { color: '#f0f0f0' },
        ticks: {
          callback: (value) => `${value}%`,
          font: { size: 10 },
        },
        title: {
          display: true,
          text: 'Target Allocation (%)',
          font: { size: 11 },
        },
      },
    },
  };

  if (isLoadingPortfolios) {
    return (
      <div className="portfolio-viewer-container">
        <div className="loading-spinner">Loading portfolios...</div>
      </div>
    );
  }

  return (
    <div className="portfolio-viewer-container" ref={containerRef}>
      <div className="viewer-header">
        <div className="header-left">
          <h2>Portfolio Viewer</h2>
        </div>
        {/* Suite-based selector: a "Select Portfolios" modal for a custom cross-suite
            set, plus Clear All. Same for every user (auth-scoped server-side). */}
        <div className="header-filters">
          <button
            className="select-all-btn"
            onClick={() => { setShowPortfolioModal(true); setModalSearch(''); }}
          >
            Select Portfolios ({selectedPortfolios.length}/{portfolios.length})
          </button>
          <button
            className="select-all-btn"
            onClick={() => setSelectedPortfolios([])}
            disabled={selectedPortfolios.length === 0}
          >
            Clear All
          </button>
        </div>
      </div>

      {/* Suite tabs — pick a suite, then choose models within it */}
      {availableTabs.length > 1 && (
        <div className="pviewer-suite-tabs">
          {availableTabs.map((tab) => (
            <button
              key={tab.label}
              className={`pviewer-suite-tab${selectedSuite === tab.label ? ' active' : ''}`}
              onClick={() => { setSelectedSuite(tab.label); setSelectedPortfolios(tab.codes); }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {/* Model chips for the active suite — one canonical column each; the ▾ opens a
          platform drill to compare that model across its platform vehicles. */}
      {activeModels.length > 0 && (
        <div className="pviewer-model-row">
          {activeModels.map((m) => {
            const vehicles = vehiclesByModel[m.blenderCode] || [];
            const active = modelIsActive(m);
            return (
              <div key={m.blenderCode || m.code} className={`pviewer-model-chip${active ? ' on' : ''}`}>
                <button className="pviewer-model-label" onClick={() => toggleModel(m)} title={m.code}>
                  {m.name}
                </button>
                {vehicles.length > 1 && (
                  <button className="pviewer-model-drill" title="Compare across platforms"
                    onClick={() => setOpenDrill(openDrill === m.blenderCode ? null : m.blenderCode)}>▾</button>
                )}
                {openDrill === m.blenderCode && vehicles.length > 1 && (
                  <div className="pviewer-drill-menu" onMouseLeave={() => setOpenDrill(null)}>
                    <div className="pviewer-drill-head">Platforms — {m.name}</div>
                    {vehicles.slice().sort((a, b) => (a.platform || '').localeCompare(b.platform || ''))
                      .map((v) => (
                        <label key={v.portfolio_code} className="pviewer-drill-item">
                          <input type="checkbox"
                            checked={selectedPortfolios.includes(v.portfolio_code)}
                            onChange={() => toggleVehicle(v.portfolio_code)} />
                          <span className="pviewer-drill-text">
                            <span className="pviewer-drill-name">{v.display_name || v.portfolio_name}</span>
                            <span className="pviewer-drill-meta">
                              <span className="pviewer-drill-plat">{v.platform || 'Unknown'}</span>
                              <span className="pviewer-drill-code">{v.portfolio_code}</span>
                            </span>
                          </span>
                        </label>
                      ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Tailored: toggle by SERIES (Altura / Derwent / Pathway …), not per portfolio */}
      {selectedSuite === 'Tailored' && tailoredSeries.length > 0 && (
        <div className="pviewer-model-row">
          {tailoredSeries.map((s) => (
            <button
              key={s.label}
              className={`pviewer-series-chip${seriesIsActive(s) ? ' on' : ''}`}
              onClick={() => toggleSeries(s)}
              title={`${s.clients.length ? `${s.clients.join(', ')} · ` : ''}${s.models.length} portfolio${s.models.length === 1 ? '' : 's'}`}
            >
              {s.label}
              {isDrummondUser && s.clients.length > 0 && (
                <span className="pviewer-series-client">{s.clients.join(', ')}</span>
              )}
              <span className="pviewer-series-count">{s.models.length}</span>
            </button>
          ))}
        </div>
      )}

      {/* Portfolio Multi-Select */}
      <div className="portfolio-selector-section">

        {/* Portfolio slicer: filter by series / platform / risk profile, then pick */}
        {showPortfolioModal && (
          /* Closes only via the × or Done: a click on the backdrop is ignored so a slip
             while slicing doesn't throw the selection work away. */
          <div className="portfolio-modal-overlay">
            <div className="portfolio-modal slicer">
              <div className="portfolio-modal-header">
                <h3>Select portfolios</h3>
                <button className="portfolio-modal-close" onClick={() => setShowPortfolioModal(false)}>×</button>
              </div>

              <input
                className="portfolio-modal-search"
                type="text"
                placeholder="Search by name, code or platform…"
                value={modalSearch}
                onChange={(e) => setModalSearch(e.target.value)}
                autoFocus
              />

              <div className="slicer-facets">
                {[['Series', seriesOptions, sliceSeries, setSliceSeries],
                  ['Platform', platformOptions, slicePlatforms, setSlicePlatforms],
                  ['Master', masterOptions, sliceMasters, setSliceMasters],
                  ['Client', clientOptions, sliceClients, setSliceClients]].map(
                  ([label, options, selected, setter]) => options.length > 1 && (
                    <div key={label} className="slicer-dropdown">
                      <button
                        className={`slicer-dd-btn${selected.length ? ' has' : ''}${openFacet === label ? ' open' : ''}`}
                        onClick={() => setOpenFacet(openFacet === label ? null : label)}>
                        {label}{selected.length ? ` · ${selected.length}` : ''}
                        <span className="slicer-dd-caret">▾</span>
                      </button>
                      {openFacet === label && (
                        <div className="slicer-dd-menu">
                          {selected.length > 0 && (
                            <button className="slicer-dd-clear" onClick={() => setter([])}>Clear</button>
                          )}
                          {options.map((opt) => (
                            <label key={opt.value} className="slicer-dd-item">
                              <input type="checkbox"
                                checked={selected.includes(opt.value)}
                                onChange={() => toggleFacet(setter)(opt.value)} />
                              <span>{opt.label}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                {(sliceSeries.length || slicePlatforms.length || sliceMasters.length || sliceClients.length) > 0 && (
                  <button className="slicer-clear" onClick={clearSlices}>Clear all filters</button>
                )}
              </div>

              <div className="portfolio-modal-list">
                {modalGroups.map(group => (
                  <div key={group.label} className="portfolio-modal-group">
                    <div className="portfolio-modal-group-head">
                      {group.label}
                      <span className="portfolio-modal-group-count">{group.portfolios.length}</span>
                    </div>
                    {group.portfolios.map(p => (
                      <label key={p.portfolio_code} className={`portfolio-modal-item${selectedPortfolios.includes(p.portfolio_code) ? ' checked' : ''}`}>
                        <input
                          type="checkbox"
                          checked={selectedPortfolios.includes(p.portfolio_code)}
                          onChange={() => handlePortfolioToggle(p.portfolio_code)}
                        />
                        <span className="portfolio-modal-name">{p.display_name || p.portfolio_name}</span>
                        <span className="portfolio-modal-platform">{p.platform || 'Unknown'}</span>
                        <span className="portfolio-modal-code">{p.portfolio_code}</span>
                      </label>
                    ))}
                  </div>
                ))}
                {modalGroups.length === 0 && (
                  <div style={{ padding: '12px', color: '#6c757d', fontSize: '0.85rem' }}>No portfolios found</div>
                )}
              </div>

              <div className="portfolio-modal-footer">
                <span className="portfolio-modal-count">
                  <strong>{selectedPortfolios.length}</strong> selected
                </span>
                <div className="portfolio-modal-actions">
                  <button className="select-all-btn" onClick={() => setSelectedPortfolios([])}>Clear All</button>
                  <button
                    className="select-all-btn"
                    disabled={!modalVisibleCodes.length}
                    onClick={() => {
                      setSelectedPortfolios(prev => [...new Set([...prev, ...modalVisibleCodes])]);
                    }}
                  >
                    Add {modalVisibleCodes.length} shown
                  </button>
                  <button className="select-all-btn primary" onClick={() => setShowPortfolioModal(false)}>
                    Done
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>

      {selectedPortfolios.length > 0 && (
        <>
          {isLoadingData ? (
            <div className="loading-spinner">Loading portfolio data...</div>
          ) : (
            <div className="tabs-container">
              {/* Tab Navigation */}
              <div className="tab-nav">
                <button
                  className={`tab-btn ${activeTab === 'holdings' ? 'active' : ''}`}
                  onClick={() => setActiveTab('holdings')}
                >
                  Target Weights
                </button>
                <button
                  className={`tab-btn ${activeTab === 'performance' ? 'active' : ''}`}
                  onClick={() => setActiveTab('performance')}
                >
                  Performance
                </button>
                <button
                  className={`tab-btn ${activeTab === 'history' ? 'active' : ''}`}
                  onClick={() => setActiveTab('history')}
                >
                  Allocation History
                </button>
                <button
                  className={`tab-btn ${activeTab === 'tilts' ? 'active' : ''}`}
                  onClick={() => setActiveTab('tilts')}
                >
                  SAA Tilts
                </button>
                <button
                  className={`tab-btn ${activeTab === 'holdings-history' ? 'active' : ''}`}
                  onClick={() => setActiveTab('holdings-history')}
                >
                  Holdings History
                </button>
                {activeTab === 'holdings' && holdingsData?.holdings?.length > 0 && (
                  <button
                    className="tab-btn export-btn"
                    onClick={exportHoldingsImage}
                    title="Download the holdings table as an image (excludes performance and report columns)"
                  >
                    Export Image
                  </button>
                )}
                {activeTab === 'performance' && sortedPerformancePortfolios.length > 0 && (
                  <button
                    className="tab-btn export-btn"
                    onClick={exportPerformanceImage}
                    title="Download the performance table as an image"
                  >
                    Export Image
                  </button>
                )}
                {holdingsData?.holdings?.length > 0 && (
                  <button
                    className="tab-btn export-btn"
                    onClick={exportToExcel}
                    title={`Export Holdings, Performance${isDrummondUser ? ', Monthly series' : ''} and Holdings History to Excel`}
                  >
                    Export Excel
                  </button>
                )}
              </div>

              {/* Tab Content */}
              <div className="tab-content">
                {/* Holdings Tab */}
                {activeTab === 'holdings' && (
                  <div className="tab-panel">
                    {holdingsData && holdingsData.holdings.length > 0 ? (
                      <div className="table-container holdings-table-container">
                        <table className="holdings-table" ref={holdingsTableRef}>
                          <thead>
                            <tr>
                              <th className="name-col">Fund Name</th>
                              <th className="code-col">APIR</th>
                              <th className="asat-col" title="Month-end the fund's returns are calculated to — lagged private markets funds sit at an earlier month than the rest of the table">As At</th>
                              {perfColumns.map(col => (
                                <th key={col.key} className="perf-col">{col.label}</th>
                              ))}
                              <th className="report-col">Report</th>
                              {sortedHoldingsPortfolios.map(p => {
                                const active = holdingsSortColEffective === p.code;
                                return (
                                  <th key={p.code}
                                    className="weight-col sortable"
                                    title="Sort holdings by this portfolio's weights"
                                    onClick={() => {
                                      if (active) setHoldingsSortDir(d => (d === 'desc' ? 'asc' : 'desc'));
                                      else { setHoldingsSortCol(p.code); setHoldingsSortDir('desc'); }
                                    }}>
                                    {getDisplayName(p.code)}
                                  </th>
                                );
                              })}
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(groupedHoldings).map(([assetClass, holdings], groupIndex) => (
                              <React.Fragment key={assetClass}>
                                {/* Asset Class Header Row */}
                                <tr className="asset-class-header">
                                  <td colSpan={11}>
                                    <span
                                      className="asset-class-indicator"
                                      style={{ backgroundColor: getAssetClassColor(assetClass, groupIndex) }}
                                    />
                                    {assetClass}
                                  </td>
                                  {sortedHoldingsPortfolios.map(p => (
                                    <td key={p.code} className="weight-col asset-class-total">
                                      {assetClassTotals[assetClass]?.[p.code]?.toFixed(1) || '0.0'}%
                                    </td>
                                  ))}
                                </tr>
                                {/* Holdings Rows */}
                                {holdings.map((holding, index) => (
                                  <tr key={`${assetClass}-${index}`} className="holding-row">
                                    <td className="name-col">{holding.asset_name}</td>
                                    <td className="code-col">{holding.apir_code}</td>
                                    <td
                                      className="asat-col"
                                      title={screenerPerfData[holding.tr_code]?.as_at_date
                                        ? `Returns to ${screenerPerfData[holding.tr_code].as_at_date}`
                                        : undefined}
                                    >
                                      {formatAsAtMonth(screenerPerfData[holding.tr_code]?.as_at_date)}
                                    </td>
                                    {perfColumns.map(col => {
                                      const fundPerf = screenerPerfData[holding.tr_code];
                                      const val = fundPerf ? fundPerf[col.key] : null;
                                      const perfClass = val > 0 ? 'perf-positive' : val < 0 ? 'perf-negative' : '';
                                      return (
                                        <td key={col.key} className={`perf-col ${perfClass}`}>
                                          {fundPerf ? col.format(val) : '-'}
                                        </td>
                                      );
                                    })}
                                    <td className="report-col">
                                      {holding.apir_code && fundsWithReports.has(holding.apir_code) && (
                                        <button
                                          className="report-link-btn"
                                          onClick={() => setOpenReportModals(prev => prev.includes(holding.apir_code) ? prev : [...prev, holding.apir_code])}
                                          title="View Research Report"
                                        >
                                          📄
                                        </button>
                                      )}
                                    </td>
                                    {sortedHoldingsPortfolios.map(p => (
                                      <td key={p.code} className="weight-col">
                                        {holding.weights[p.code] !== undefined
                                          ? `${holding.weights[p.code].toFixed(1)}%`
                                          : '-'}
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                              </React.Fragment>
                            ))}
                            {/* Total Row */}
                            <tr className="total-row">
                              <td colSpan={11}>Total</td>
                              {sortedHoldingsPortfolios.map(p => (
                                <td key={p.code} className="weight-col">
                                  {portfolioTotals[p.code]?.toFixed(1)}%
                                </td>
                              ))}
                            </tr>
                            {/* Summary Metrics Rows */}
                            {holdingsData.summary && (
                              <>
                                <tr className="metric-row">
                                  <td colSpan={11}>FX Exposure</td>
                                  {sortedHoldingsPortfolios.map(p => (
                                    <td key={p.code} className="weight-col">
                                      {holdingsData.summary[p.code]?.fx_exposure?.toFixed(1) || '0.0'}%
                                    </td>
                                  ))}
                                </tr>
                                <tr className="metric-row growth-row">
                                  <td colSpan={11}>Growth Exposure</td>
                                  {sortedHoldingsPortfolios.map(p => (
                                    <td key={p.code} className="weight-col">
                                      {holdingsData.summary[p.code]?.growth_exposure?.toFixed(1) || '0.0'}%
                                    </td>
                                  ))}
                                </tr>
                                <tr className="metric-row defensive-row">
                                  <td colSpan={11}>Defensive Exposure</td>
                                  {sortedHoldingsPortfolios.map(p => (
                                    <td key={p.code} className="weight-col">
                                      {holdingsData.summary[p.code]?.defensive_exposure?.toFixed(1) || '0.0'}%
                                    </td>
                                  ))}
                                </tr>
                                {/* Weighted fee rows from the trading app's fund-level fee / rebate store — Drummond users only */}
                                {isDrummondUser && (
                                  <>
                                    <tr className="metric-row fee-row">
                                      <td colSpan={11} title="Weighted-average cost: Σ (weight ÷ 100 × Management Fees & Costs %) — manual override, else FE fee table">Fees &amp; Costs (wtd)</td>
                                      {sortedHoldingsPortfolios.map(p => (
                                        <td key={p.code} className="weight-col">
                                          {holdingsData.summary[p.code]?.fees_costs_wtd != null ? `${holdingsData.summary[p.code].fees_costs_wtd.toFixed(2)}%` : '-'}
                                        </td>
                                      ))}
                                    </tr>
                                    <tr className="metric-row fee-row net-row">
                                      <td colSpan={11} title="Weighted-average cost after rebates: Σ (weight ÷ 100 × (Management Fees & Costs − rebate))">Net (wtd)</td>
                                      {sortedHoldingsPortfolios.map(p => (
                                        <td key={p.code} className="weight-col">
                                          {holdingsData.summary[p.code]?.net_wtd != null ? `${holdingsData.summary[p.code].net_wtd.toFixed(2)}%` : '-'}
                                        </td>
                                      ))}
                                    </tr>
                                  </>
                                )}
                              </>
                            )}
                            {/* SAA tilts by asset class group (target − SAA neutral), one bar per portfolio column — Drummond users only */}
                            {isDrummondUser && tiltGroups.items.length > 0 && (
                              <>
                                <tr className="pv-tilt-head">
                                  <td colSpan={11}
                                    title="Deviation from the strategic asset allocation neutral — bar right = overweight, left = underweight">
                                    SAA tilts
                                  </td>
                                  {sortedHoldingsPortfolios.map((p) => <td key={p.code} className="weight-col" />)}
                                </tr>
                                {tiltGroups.items.map((acg) => {
                                  const colour = tiltAcColour(acg, tiltAcRank(acg));
                                  return (
                                    <tr key={`tilt-${acg}`} className="pv-tilt-ac">
                                      <td colSpan={11}>
                                        <span className="ac-dot" style={{ background: colour }} />{acg}
                                      </td>
                                      {sortedHoldingsPortfolios.map((p) => {
                                        const v = (tiltsByCode[p.code] || {})[acg];
                                        return (
                                          <td key={p.code} className="weight-col pv-tilt-cell">
                                            {v == null ? null : <TiltBar value={v} max={tiltGroups.max} color={colour} />}
                                          </td>
                                        );
                                      })}
                                    </tr>
                                  );
                                })}
                              </>
                            )}
                            {/* Regional tilts (look-through vs MSCI ACWI) — same table, regions down the left */}
                            {isDrummondUser && regionalTilts.has && (
                              <>
                                <tr className="pv-tilt-head pv-tilt-head-region">
                                  <td colSpan={11}
                                    title="Portfolio equity regional exposure (look-through) vs the MSCI ACWI benchmark">
                                    Equity Region Tilts
                                  </td>
                                  {sortedHoldingsPortfolios.map((p) => <td key={p.code} className="weight-col" />)}
                                </tr>
                                {regionalTilts.regions.map((region, i) => {
                                  const colour = regionColour(region, i);
                                  return (
                                    <tr key={`region-${region}`} className="pv-tilt-ac">
                                      <td colSpan={11}>
                                        <span className="ac-dot" style={{ background: colour }} />{region}
                                      </td>
                                      {sortedHoldingsPortfolios.map((p) => {
                                        const v = regionalTilts.byCodeRegion[p.code]?.[region];
                                        return (
                                          <td key={p.code} className="weight-col pv-tilt-cell">
                                            {v == null ? null : <TiltBar value={v} max={regionalTilts.max} color={colour} suffix="% vs MSCI ACWI" />}
                                          </td>
                                        );
                                      })}
                                    </tr>
                                  );
                                })}
                              </>
                            )}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="no-data-message">
                        <p>No holdings data available for the selected portfolio(s).</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Performance Tab */}
                {activeTab === 'performance' && (
                  <div className="tab-panel" ref={performancePanelRef}>
                    <div className="perf-view-toggle">
                      <button className={`perf-view-btn${perfView === 'periods' ? ' active' : ''}`}
                        onClick={() => setPerfView('periods')}>Periods</button>
                      <button className={`perf-view-btn${perfView === 'fy' ? ' active' : ''}`}
                        onClick={() => setPerfView('fy')}>Financial Year</button>
                      <button className={`perf-view-btn${perfView === 'risk' ? ' active' : ''}`}
                        onClick={() => setPerfView('risk')}>Risk</button>
                      <button className={`perf-view-btn${perfView === 'custom' ? ' active' : ''}`}
                        onClick={() => setPerfView('custom')}>Custom Period</button>
                      {isDrummondUser && (
                        <button className={`perf-view-btn${perfView === 'monthly' ? ' active' : ''}`}
                          onClick={() => setPerfView('monthly')}
                          title="Drummond only: cumulative return line, month-on-month returns as bars and a table">Monthly</button>
                      )}
                    </div>
                    {isDrummondUser && (
                      <div className="perf-view-toggle perf-bench-toggle"
                        title="Drummond only: compare each portfolio with its assigned benchmark or its CPI+ return objective (Investment Config)">
                        <button className={`perf-view-btn${benchMode === 'assigned' ? ' active' : ''}`}
                          onClick={() => setBenchMode('assigned')}>vs Assigned Benchmark</button>
                        <button className={`perf-view-btn${benchMode === 'objective' ? ' active' : ''}`}
                          onClick={() => setBenchMode('objective')}>vs CPI+ Objective</button>
                      </div>
                    )}

                    {perfView === 'periods' && (<>
                    {performanceData && sortedPerformancePortfolios.length > 0 && (
                      perfDatesUniform
                        ? (performanceData.as_at_date && (
                            <p className="perf-as-at">As at {performanceData.as_at_date}</p>
                          ))
                        : (
                            <p className="perf-as-at">
                              As at dates vary by portfolio — each row is shown as at that portfolio&apos;s latest audited month.
                            </p>
                          )
                    )}
                    {performanceData && sortedPerformancePortfolios.length > 0 ? (
                      renderPeriodTable(sortedPerformancePortfolios, performanceData.periods,
                        (p) => (!perfDatesUniform && p.as_at_date ? `As at ${p.as_at_date}` : null))
                    ) : (
                      <div className="no-data-message">
                        <p>No performance data available for the selected portfolio(s).</p>
                      </div>
                    )}
                    </>)}

                    {/* ── Financial Year view ── */}
                    {perfView === 'fy' && (
                      loadingFY ? (
                        <div className="chart-loading">Loading financial year returns...</div>
                      ) : fyColumns.length > 0 && fyVisibleCodes.length > 0 ? (
                        <>
                        <div className="table-container">
                          <table className="performance-table">
                            <thead>
                              <tr>
                                <th className="portfolio-col">Portfolio</th>
                                <th className="metric-col">Metric</th>
                                {fyColumns.map((col) => (
                                  <th key={col.key} className="period-col">{col.label}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {fyVisibleCodes.map((code, idx) => {
                                const fyData = fyReturnsData[code];
                                if (!fyData?.columns) return null;
                                const colMap = {};
                                fyData.columns.forEach((c) => { colMap[c.key] = c; });
                                return (
                                  <React.Fragment key={code}>
                                    <tr className={`perf-row portfolio-row ${idx > 0 ? 'new-portfolio' : ''}`}>
                                      <td rowSpan={3} className="portfolio-col">
                                        <div className="portfolio-info">
                                          <span className="portfolio-name">{getDisplayName(code)}</span>
                                          <span className="ami-name">vs {benchLabelFor(code, 'Peer Benchmark')}</span>
                                        </div>
                                      </td>
                                      <td className="metric-col">Portfolio</td>
                                      {fyColumns.map((col) => {
                                        const cell = colMap[col.key];
                                        return (
                                          <td key={col.key} className="period-col">
                                            {cell?.portfolio != null
                                              ? `${cell.portfolio.toFixed(1)}%${cell.partial ? '*' : ''}`
                                              : '-'}
                                          </td>
                                        );
                                      })}
                                    </tr>
                                    <tr className="perf-row ami-row">
                                      <td className="metric-col">Benchmark</td>
                                      {fyColumns.map((col) => {
                                        const cell = colMap[col.key];
                                        return (
                                          <td key={col.key} className="period-col">
                                            {cell?.peer != null
                                              ? `${cell.peer.toFixed(1)}%${cell.partial ? '*' : ''}`
                                              : '-'}
                                          </td>
                                        );
                                      })}
                                    </tr>
                                    <tr className="perf-row diff-row">
                                      <td className="metric-col">Difference</td>
                                      {fyColumns.map((col) => {
                                        const cell = colMap[col.key];
                                        const diff = cell?.portfolio != null && cell?.peer != null
                                          ? cell.portfolio - cell.peer : null;
                                        return (
                                          <td key={col.key}
                                            className={`period-col diff-value ${diff != null ? (diff >= 0 ? 'positive' : 'negative') : ''}`}>
                                            {diff != null ? `${diff >= 0 ? '+' : ''}${diff.toFixed(1)}%` : '-'}
                                          </td>
                                        );
                                      })}
                                    </tr>
                                  </React.Fragment>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                        {/* Partial-year footnotes */}
                        {(() => {
                          const lastFyKey = fyColumns[fyColumns.length - 1]?.key;
                          const fmtAsAt = (iso) => {
                            if (!iso) return null;
                            const d = new Date(`${String(iso).slice(0, 10)}T00:00:00`);
                            return isNaN(d) ? null : d.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
                          };
                          const footnotes = [];
                          fyVisibleCodes.forEach((code) => {
                            const cols = fyReturnsData[code]?.columns;
                            if (!cols) return;
                            const partialCols = cols.filter((c) => c.partial && c.portfolio != null);
                            if (partialCols.length === 0) return;
                            const name = getDisplayName(code);
                            const partialKeys = partialCols.map((c) => c.key);
                            const hasCurrentFY = partialKeys.includes(lastFyKey);
                            const inceptionPartials = partialKeys.filter((k) => k !== lastFyKey);
                            const asAt = fmtAsAt(partialCols.find((c) => c.key === lastFyKey)?.as_at);
                            const ytdText = asAt ? `${lastFyKey} year-to-date to ${asAt}` : `${lastFyKey} year-to-date`;
                            if (inceptionPartials.length > 0 && hasCurrentFY) {
                              footnotes.push(`${name}: ${inceptionPartials.join(', ')} partial (inception mid-year), ${ytdText}`);
                            } else if (inceptionPartials.length > 0) {
                              footnotes.push(`${name}: ${inceptionPartials.join(', ')} partial (inception mid-year)`);
                            } else if (hasCurrentFY) {
                              footnotes.push(`${name}: ${ytdText}`);
                            }
                          });
                          if (footnotes.length === 0) return null;
                          const allOnlyCurrentFY = fyVisibleCodes.every((code) => {
                            const cols = fyReturnsData[code]?.columns;
                            if (!cols) return true;
                            const partials = cols.filter((c) => c.partial && c.portfolio != null);
                            return partials.length <= 1 && partials.every((c) => c.key === lastFyKey);
                          });
                          if (allOnlyCurrentFY) {
                            const asAtSet = new Set(fyVisibleCodes
                              .map((code) => (fyReturnsData[code]?.columns || [])
                                .find((c) => c.key === lastFyKey && c.partial)?.as_at)
                              .filter(Boolean));
                            const commonAsAt = asAtSet.size === 1 ? fmtAsAt([...asAtSet][0]) : null;
                            return (
                              <div className="perf-footnotes">
                                <p>*{lastFyKey} returns are year-to-date{commonAsAt ? ` to ${commonAsAt}` : ''}</p>
                              </div>
                            );
                          }
                          return (
                            <div className="perf-footnotes">
                              {footnotes.map((note, i) => <p key={i}>*{note}</p>)}
                            </div>
                          );
                        })()}
                        {/* Missing-month footnotes: FYs spanning a month with no reported
                            return are left blank rather than assuming a flat month */}
                        {(() => {
                          const notes = fyGapNotes(fyVisibleCodes, fyReturnsData, getDisplayName);
                          return notes.length ? (
                            <div className="perf-footnotes">{notes.map((n, i) => <p key={i}>{n}</p>)}</div>
                          ) : null;
                        })()}
                        </>
                      ) : (
                        <div className="no-data-message">
                          <p>No financial year data available for the selected portfolio(s).</p>
                        </div>
                      )
                    )}

                    {/* ── Risk view ── */}
                    {perfView === 'risk' && (
                      loadingRisk ? (
                        <div className="chart-loading">Loading risk metrics...</div>
                      ) : riskVisibleCodes.length > 0 ? (
                        <div className="table-container">
                          <table className="performance-table">
                            <thead>
                              <tr>
                                <th className="portfolio-col">Portfolio</th>
                                <th className="metric-col">Metric</th>
                                <th className="period-col">Tracking Error</th>
                                <th className="period-col">Volatility</th>
                                <th className="period-col">Return / Risk</th>
                                <th className="period-col">Max Drawdown</th>
                              </tr>
                            </thead>
                            <tbody>
                              {riskVisibleCodes.map((code, idx) => {
                                const d = riskMetricsData[code];
                                const volDiff = (d.volatility != null && d.benchmark_volatility != null)
                                  ? d.volatility - d.benchmark_volatility : null;
                                const rrDiff = (d.return_risk_ratio != null && d.benchmark_return_risk_ratio != null)
                                  ? d.return_risk_ratio - d.benchmark_return_risk_ratio : null;
                                const ddDiff = (d.max_drawdown != null && d.benchmark_max_drawdown != null)
                                  ? d.max_drawdown - d.benchmark_max_drawdown : null;
                                return (
                                  <React.Fragment key={code}>
                                    <tr className={`perf-row portfolio-row ${idx > 0 ? 'new-portfolio' : ''}`}>
                                      <td rowSpan={3} className="portfolio-col">
                                        <div className="portfolio-info">
                                          <span className="portfolio-name">{getDisplayName(code)}</span>
                                          <span className="ami-name">vs {benchLabelFor(code, 'Benchmark')}</span>
                                        </div>
                                      </td>
                                      <td className="metric-col">Portfolio</td>
                                      <td className="period-col">{d.tracking_error != null ? `${d.tracking_error.toFixed(1)}%` : '-'}</td>
                                      <td className="period-col">{d.volatility != null ? `${d.volatility.toFixed(1)}%` : '-'}</td>
                                      <td className="period-col">{d.return_risk_ratio != null ? d.return_risk_ratio.toFixed(2) : '-'}</td>
                                      <td className="period-col">{d.max_drawdown != null ? `${d.max_drawdown.toFixed(1)}%` : '-'}</td>
                                    </tr>
                                    <tr className="perf-row ami-row">
                                      <td className="metric-col">Benchmark</td>
                                      <td className="period-col"></td>
                                      <td className="period-col">{d.benchmark_volatility != null ? `${d.benchmark_volatility.toFixed(1)}%` : '-'}</td>
                                      <td className="period-col">{d.benchmark_return_risk_ratio != null ? d.benchmark_return_risk_ratio.toFixed(2) : '-'}</td>
                                      <td className="period-col">{d.benchmark_max_drawdown != null ? `${d.benchmark_max_drawdown.toFixed(1)}%` : '-'}</td>
                                    </tr>
                                    <tr className="perf-row diff-row">
                                      <td className="metric-col">Difference</td>
                                      <td className="period-col diff-value"></td>
                                      <td className={`period-col diff-value ${volDiff > 0 ? 'negative' : volDiff < 0 ? 'positive' : ''}`}>
                                        {volDiff != null ? `${volDiff.toFixed(1)}%` : '-'}
                                      </td>
                                      <td className={`period-col diff-value ${rrDiff > 0 ? 'positive' : rrDiff < 0 ? 'negative' : ''}`}>
                                        {rrDiff != null ? rrDiff.toFixed(2) : '-'}
                                      </td>
                                      <td className={`period-col diff-value ${ddDiff > 0 ? 'positive' : ddDiff < 0 ? 'negative' : ''}`}>
                                        {ddDiff != null ? `${ddDiff.toFixed(1)}%` : '-'}
                                      </td>
                                    </tr>
                                  </React.Fragment>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="no-data-message">
                          <p>No risk metrics available for the selected portfolio(s).</p>
                        </div>
                      )
                    )}
                    {perfView === 'risk' && !loadingRisk && riskVisibleCodes.length > 0 && (
                      <div className="perf-footnotes">
                        <p>{RISK_METHOD_NOTE}</p>
                        {riskGapNotes(riskVisibleCodes, riskMetricsData, getDisplayName).map((n, i) => <p key={i}>{n}</p>)}
                      </div>
                    )}

                    {/* ── Custom Period view ── */}
                    {perfView === 'custom' && (<>
                      {customRangePicker}
                      {customError ? (
                        <div className="no-data-message"><p>Custom period returns failed: {customError}</p></div>
                      ) : loadingCustom || !customData ? (
                        <div className="chart-loading">Loading custom period returns...</div>
                      ) : customVisibleCodes.length > 0 ? (<>
                        {renderPeriodTable(
                          customVisibleCodes.map((code) => ({
                            portfolio_code: code,
                            ami_benchmark: customData[code].benchmark_name || codeToBenchmark[code],
                            objective_missing: benchMode === 'objective' && !customData[code].benchmark_name,
                            performance: customData[code].performance || {},
                            first_month: customData[code].first_month,
                            last_month: customData[code].last_month,
                            gap_months: customData[code].gap_months || [],
                          })),
                          customPeriods,
                          (p) => {
                            const notes = [];
                            if (customStart < p.first_month || customEnd > p.last_month) {
                              notes.push(`Data available ${fmtMonth(p.first_month)} to ${fmtMonth(p.last_month)}`);
                            }
                            const gaps = p.gap_months.filter((g) => g <= customEnd);
                            if (gaps.length) {
                              notes.push(`No return reported for ${gaps.map(fmtMonth).join(', ')}; periods including it are blank`);
                            }
                            return notes.length ? notes.join(' · ') : null;
                          })}
                        <div className="perf-footnotes">
                          <p>Recalculated from the monthly return series as at the end of {fmtMonth(customEnd)}, using only
                            months from the start of {fmtMonth(customStart)}. Periods longer than the selected window show a
                            dash. Since Start runs over the whole window; with the start at inception it equals the Inception
                            columns on the Periods view.</p>
                        </div>
                      </>
                      ) : (
                        <div className="no-data-message">
                          <p>No performance data available for the selected portfolio(s).</p>
                        </div>
                      )}
                    </>)}

                    {/* ── Monthly view (Drummond only) ── */}
                    {perfView === 'monthly' && isDrummondUser && (<>
                      {customRangePicker}
                      <div className="perf-monthly-controls">
                        <label className="perf-monthly-toggle">
                          <input type="checkbox" checked={monthlyShowBench}
                            onChange={(e) => setMonthlyShowBench(e.target.checked)} />
                          Show benchmark series
                        </label>
                      </div>
                      {customError ? (
                        <div className="no-data-message"><p>Monthly returns failed: {customError}</p></div>
                      ) : loadingCustom || !customData ? (
                        <div className="chart-loading">Loading monthly returns...</div>
                      ) : monthlyCodes.length > 0 && monthlyLineData && monthlyBarData ? (<>
                        <div className="perf-monthly-charts">
                          <div className="perf-chart-box">
                            <h4>Cumulative return since {fmtMonth(customStart)}</h4>
                            <div className="chart-container">
                              <Line data={monthlyLineData} options={monthlyChartOptions('Cumulative return (%)')} />
                            </div>
                          </div>
                          <div className="perf-chart-box">
                            <h4>Drawdown from peak since {fmtMonth(customStart)}</h4>
                            <div className="chart-container">
                              <Line data={monthlyDrawdownData} options={monthlyChartOptions('Drawdown (%)', { suggestedMax: 0 })} />
                            </div>
                          </div>
                          <div className="perf-chart-box">
                            <h4>Cumulative alpha (sum of monthly excess returns)</h4>
                            {hasAnyPoint(monthlyAlphaData) ? (
                              <div className="chart-container">
                                <Line data={monthlyAlphaData} options={monthlyChartOptions('Cumulative alpha (%)')} />
                              </div>
                            ) : (
                              <div className="chart-loading">No benchmark series for the selected portfolio(s).</div>
                            )}
                          </div>
                          <div className="perf-chart-box">
                            <h4>Cumulative return relative to benchmark (geometric)</h4>
                            {hasAnyPoint(monthlyRelativeData) ? (
                              <div className="chart-container">
                                <Line data={monthlyRelativeData} options={monthlyChartOptions('Relative return (%)')} />
                              </div>
                            ) : (
                              <div className="chart-loading">No benchmark series for the selected portfolio(s).</div>
                            )}
                          </div>
                          <div className="perf-chart-box wide">
                            <h4>Rolling 12-month return</h4>
                            {hasAnyPoint(monthlyRollingData) ? (
                              <div className="chart-container">
                                <Line data={monthlyRollingData} options={monthlyChartOptions('Rolling 12-month return (%)')} />
                              </div>
                            ) : (
                              <div className="chart-loading">No month in this window has 12 months of reported history behind it.</div>
                            )}
                          </div>
                          <div className="perf-chart-box wide">
                            <h4>Month-on-month return</h4>
                            <div className="chart-container">
                              <Bar data={monthlyBarData} options={monthlyChartOptions('Monthly return (%)')} />
                            </div>
                          </div>
                        </div>
                        <div className="table-container">
                          <table className="performance-table perf-monthly-table">
                            <thead>
                              <tr>
                                <th rowSpan={2} className="month-col">Month</th>
                                {monthlyCodes.map((code) => (
                                  <th key={code} colSpan={3} className="group-col">
                                    <div className="portfolio-info">
                                      <span>{getDisplayName(code)}</span>
                                      <span className="group-bench">vs {customData[code].benchmark_name || codeToBenchmark[code] || 'Benchmark'}</span>
                                    </div>
                                  </th>
                                ))}
                              </tr>
                              <tr>
                                {monthlyCodes.map((code) => (
                                  <React.Fragment key={code}>
                                    <th className="period-col sub-col">Portfolio</th>
                                    <th className="period-col sub-col">Benchmark</th>
                                    <th className="period-col sub-col">+/-</th>
                                  </React.Fragment>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {[...monthlyLabels].reverse().map((m) => (
                                <tr key={m} className="perf-row">
                                  <td className="month-col">{fmtMonth(m)}</td>
                                  {monthlyCodes.map((code) => {
                                    const r = monthlyByCode[code]?.[m];
                                    if (r?.gap) {
                                      return (
                                        <React.Fragment key={code}>
                                          <td className="period-col gap-cell" title="No return reported for this month">n/r</td>
                                          <td className="period-col">{fmtPct(r.benchmark)}</td>
                                          <td className="period-col gap-cell">-</td>
                                        </React.Fragment>
                                      );
                                    }
                                    return (
                                      <React.Fragment key={code}>
                                        <td className="period-col">{fmtPct(r?.portfolio)}</td>
                                        <td className="period-col">{fmtPct(r?.benchmark)}</td>
                                        <td className={`period-col diff-value ${diffClass(r?.difference)}`}>{fmtPct(r?.difference, true)}</td>
                                      </React.Fragment>
                                    );
                                  })}
                                </tr>
                              ))}
                              <tr className="perf-row total-row">
                                <td className="month-col">Cumulative</td>
                                {monthlyCodes.map((code) => {
                                  const r = monthlyByCode[code]?.[customEnd];
                                  const diff = r?.portfolio_cum != null && r?.benchmark_cum != null
                                    ? r.portfolio_cum - r.benchmark_cum : null;
                                  return (
                                    <React.Fragment key={code}>
                                      <td className="period-col">{fmtPct(r?.portfolio_cum)}</td>
                                      <td className="period-col">{fmtPct(r?.benchmark_cum)}</td>
                                      <td className={`period-col diff-value ${diffClass(diff)}`}>{fmtPct(diff, true)}</td>
                                    </React.Fragment>
                                  );
                                })}
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <div className="perf-footnotes">
                          <p>Month-on-month returns from the stored monthly series, 1 {fmtMonth(customStart)} to end {fmtMonth(customEnd)}.
                            Cumulative compounds those months from the start of the window; a portfolio whose history begins
                            inside the window compounds from its own first month. The Cumulative row matches the Since Start
                            column on the Custom Period view (shown here to 2 decimal places). Drawdown is the fall from the
                            highest cumulative level reached since the window start (0% at a new high). Rolling 12-month
                            returns look back into history before the window start and need 12 reported months. Cumulative
                            alpha is the running sum of the monthly portfolio-minus-benchmark returns (arithmetic); relative
                            return is the geometric version, (1 + portfolio cumulative) / (1 + benchmark cumulative) - 1.</p>
                          {monthlyGapNotes.length > 0 && (
                            <p>n/r: no return reported for that month, so no month-on-month figure is shown and the stored
                              series carries the month at 0% in the cumulative line. {monthlyGapNotes.join('; ')}.</p>
                          )}
                        </div>
                      </>
                      ) : (
                        <div className="no-data-message">
                          <p>No monthly returns available for the selected portfolio(s) in this window.</p>
                        </div>
                      )}
                    </>)}

                    <div className="perf-disclaimer">
                      <ManagedDisclaimer dkey="perf_general_note" as="p" />
                      {suiteDisclaimers.map((sd) => (
                        <p key={sd.suite}>
                          <strong>{sd.suite}:</strong>{sd.inceptionText} {sd.sourceLines.join(' ')}
                        </p>
                      ))}
                    </div>
                  </div>
                )}

                {/* History Tab */}
                {activeTab === 'history' && (
                  <div className="tab-panel">
                    <div className="chart-controls">
                      <div className="chart-portfolio-selector">
                        <label htmlFor="history-portfolio">Select Portfolio:</label>
                        <select
                          id="history-portfolio"
                          value={historyPortfolio}
                          onChange={(e) => setHistoryPortfolio(e.target.value)}
                        >
                          {selectedPortfolios.map(code => {
                            const portfolio = portfolios.find(p => p.portfolio_code === code);
                            return (
                              <option key={code} value={code}>
                                {portfolio?.display_name || portfolio?.portfolio_name || code}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                    </div>
                    {chartData && chartData.datasets.length > 0 ? (
                      <div className="chart-container">
                        <Line data={chartData} options={chartOptions} />
                      </div>
                    ) : (
                      <div className="chart-loading">Loading chart data...</div>
                    )}
                  </div>
                )}

                {/* SAA Tilts Tab */}
                {activeTab === 'tilts' && (
                  <div className="tab-panel">
                    <div className="chart-controls">
                      <div className="chart-portfolio-selector">
                        <label htmlFor="tilts-portfolio">Select Portfolio:</label>
                        <select
                          id="tilts-portfolio"
                          value={tiltsPortfolio}
                          onChange={(e) => setTiltsPortfolio(e.target.value)}
                        >
                          {selectedPortfolios.map(code => {
                            const portfolio = portfolios.find(p => p.portfolio_code === code);
                            return (
                              <option key={code} value={code}>
                                {portfolio?.display_name || portfolio?.portfolio_name || code}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                    </div>
                    {tiltsData ? (
                      <div className="tilts-charts-container">
                        {/* Snapshot Bar Chart - Current Tilts */}
                        <div className="tilts-snapshot">
                          <h4>Current SAA Tilts</h4>
                          {tiltsData.latest && Object.keys(tiltsData.latest).length > 0 ? (
                            <div className="chart-container">
                              <Bar
                                data={{
                                  labels: Object.keys(tiltsData.latest).sort((a, b) =>
                                    Math.abs(tiltsData.latest[b]) - Math.abs(tiltsData.latest[a])
                                  ),
                                  datasets: [{
                                    label: 'Tilt (%)',
                                    data: Object.keys(tiltsData.latest)
                                      .sort((a, b) => Math.abs(tiltsData.latest[b]) - Math.abs(tiltsData.latest[a]))
                                      .map(ac => tiltsData.latest[ac]),
                                    backgroundColor: Object.keys(tiltsData.latest)
                                      .sort((a, b) => Math.abs(tiltsData.latest[b]) - Math.abs(tiltsData.latest[a]))
                                      .map(ac => tiltsData.latest[ac] >= 0 ? '#91CF9B' : '#dc3545'),
                                    borderColor: Object.keys(tiltsData.latest)
                                      .sort((a, b) => Math.abs(tiltsData.latest[b]) - Math.abs(tiltsData.latest[a]))
                                      .map(ac => tiltsData.latest[ac] >= 0 ? '#6db86d' : '#a71d2a'),
                                    borderWidth: 1,
                                  }]
                                }}
                                options={{
                                  indexAxis: 'y',
                                  responsive: true,
                                  maintainAspectRatio: false,
                                  plugins: {
                                    legend: { display: false },
                                    tooltip: {
                                      callbacks: {
                                        label: (context) => {
                                          const val = context.parsed.x;
                                          return `${val >= 0 ? '+' : ''}${val.toFixed(2)}% ${val >= 0 ? '(Overweight)' : '(Underweight)'}`;
                                        }
                                      }
                                    }
                                  },
                                  scales: {
                                    x: {
                                      grid: { color: '#f0f0f0' },
                                      ticks: {
                                        callback: (value) => `${value >= 0 ? '+' : ''}${value}%`,
                                        font: { size: 10 }
                                      },
                                      title: {
                                        display: true,
                                        text: 'Tilt (Target - SAA)',
                                        font: { size: 11 }
                                      }
                                    },
                                    y: {
                                      grid: { display: false },
                                      ticks: { font: { size: 10 } }
                                    }
                                  }
                                }}
                              />
                            </div>
                          ) : (
                            <div className="no-data-message">No current tilt data available</div>
                          )}
                        </div>

                        {/* Time Series Chart - Historical Tilts */}
                        <div className="tilts-time-series">
                          <h4>Historical SAA Tilts</h4>
                          {tiltsData.dates && tiltsData.dates.length > 0 ? (
                            <div className="chart-container">
                              <Bar
                                data={{
                                  labels: tiltsData.dates,
                                  datasets: Object.entries(tiltsData.series || {}).map(([assetClass, values], index) => ({
                                    label: assetClass,
                                    data: values,
                                    backgroundColor: getAssetClassColor(assetClass, index),
                                    borderColor: getAssetClassColor(assetClass, index),
                                    borderWidth: 0,
                                  }))
                                }}
                                options={{
                                  responsive: true,
                                  maintainAspectRatio: false,
                                  interaction: {
                                    mode: 'index',
                                    intersect: false,
                                  },
                                  plugins: {
                                    legend: {
                                      position: 'bottom',
                                      labels: {
                                        usePointStyle: true,
                                        padding: 15,
                                        font: { size: 11 },
                                      },
                                    },
                                    tooltip: {
                                      callbacks: {
                                        label: (context) => {
                                          const val = context.parsed.y;
                                          return `${context.dataset.label}: ${val >= 0 ? '+' : ''}${val.toFixed(2)}%`;
                                        }
                                      }
                                    }
                                  },
                                  scales: {
                                    x: {
                                      stacked: true,
                                      grid: { display: false },
                                      ticks: {
                                        maxTicksLimit: 12,
                                        font: { size: 10 },
                                      },
                                    },
                                    y: {
                                      stacked: true,
                                      grid: { color: '#f0f0f0' },
                                      ticks: {
                                        callback: (value) => `${value >= 0 ? '+' : ''}${value}%`,
                                        font: { size: 10 },
                                      },
                                      title: {
                                        display: true,
                                        text: 'Tilt (Target - SAA) %',
                                        font: { size: 11 },
                                      },
                                    },
                                  },
                                }}
                              />
                            </div>
                          ) : (
                            <div className="no-data-message">No historical tilt data available</div>
                          )}
                        </div>

                        {/* Historical Growth / Defensive tilt vs the master SAA's neutral growth */}
                        <div className="tilts-time-series">
                          <h4>Historical Growth / Defensive Tilt</h4>
                          <p className="tilts-note">Growth weight (target × each sub asset class's growth exposure) minus the
                            SAA neutral growth weight. Above zero = overweight growth / underweight defensive.</p>
                          {exposureErr ? (
                            <div className="no-data-message">Failed to load: {exposureErr}</div>
                          ) : !exposureData ? (
                            <div className="chart-loading">Loading growth / defensive history...</div>
                          ) : exposureData.dates.length === 0 ? (
                            <div className="no-data-message">No historical growth / defensive data available</div>
                          ) : (
                            <div className="chart-container">
                              <Bar
                                data={{
                                  labels: exposureData.dates,
                                  datasets: [{
                                    label: 'Growth tilt',
                                    data: exposureData.growth_tilt,
                                    backgroundColor: exposureData.growth_tilt.map((v) => (v ?? 0) >= 0 ? '#12706C' : '#AA8090'),
                                    borderWidth: 0,
                                  }],
                                }}
                                options={{
                                  responsive: true,
                                  maintainAspectRatio: false,
                                  interaction: { mode: 'index', intersect: false },
                                  plugins: {
                                    legend: { display: false },
                                    tooltip: {
                                      callbacks: {
                                        label: (ctx) => {
                                          const i = ctx.dataIndex, d = exposureData;
                                          const v = ctx.parsed.y;
                                          const out = [
                                            v == null ? 'Growth tilt: n/a (no SAA for this period)'
                                                      : `Growth tilt: ${v >= 0 ? '+' : ''}${v.toFixed(2)}%`,
                                            `Growth ${d.growth[i].toFixed(2)}% vs SAA ${d.saa_growth[i] == null ? 'n/a' : d.saa_growth[i].toFixed(2) + '%'}`,
                                            `Defensive ${d.defensive[i].toFixed(2)}%`,
                                          ];
                                          if (d.unclassified[i]) out.push(`Unclassified sub asset class ${d.unclassified[i].toFixed(2)}%`);
                                          return out;
                                        },
                                      },
                                    },
                                  },
                                  scales: {
                                    x: { grid: { display: false }, ticks: { maxTicksLimit: 12, font: { size: 10 } } },
                                    y: {
                                      grid: { color: '#f0f0f0' },
                                      ticks: { callback: (value) => `${value >= 0 ? '+' : ''}${value}%`, font: { size: 10 } },
                                      title: { display: true, text: 'Growth tilt (Target - SAA) %', font: { size: 11 } },
                                    },
                                  },
                                }}
                              />
                            </div>
                          )}
                        </div>

                        {/* Historical Active / Passive split: passive = AP List Style 'Passive' + cash */}
                        <div className="tilts-time-series">
                          <h4>Historical Active / Passive Split</h4>
                          <p className="tilts-note">Passive = funds with AP List style "Passive" or "Benchmark" (index funds) plus cash; active = all other holdings.</p>
                          {exposureErr ? (
                            <div className="no-data-message">Failed to load: {exposureErr}</div>
                          ) : !exposureData ? (
                            <div className="chart-loading">Loading active / passive history...</div>
                          ) : exposureData.dates.length === 0 ? (
                            <div className="no-data-message">No historical active / passive data available</div>
                          ) : (
                            <div className="chart-container">
                              <Bar
                                data={{
                                  labels: exposureData.dates,
                                  datasets: [
                                    { label: 'Active', data: exposureData.active, backgroundColor: '#012341', borderWidth: 0 },
                                    { label: 'Passive (incl. cash)', data: exposureData.passive, backgroundColor: '#A6C6C1', borderWidth: 0 },
                                  ],
                                }}
                                options={{
                                  responsive: true,
                                  maintainAspectRatio: false,
                                  interaction: { mode: 'index', intersect: false },
                                  plugins: {
                                    legend: { position: 'bottom', labels: { usePointStyle: true, padding: 15, font: { size: 11 } } },
                                    tooltip: {
                                      callbacks: {
                                        label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y.toFixed(2)}%`,
                                        afterBody: (items) => {
                                          const n = exposureData.not_in_ap_list[items[0]?.dataIndex];
                                          return n ? [`(${n.toFixed(2)}% of active not found in AP List)`] : [];
                                        },
                                      },
                                    },
                                  },
                                  scales: {
                                    x: { stacked: true, grid: { display: false }, ticks: { maxTicksLimit: 12, font: { size: 10 } } },
                                    y: {
                                      stacked: true,
                                      grid: { color: '#f0f0f0' },
                                      ticks: { callback: (value) => `${value}%`, font: { size: 10 } },
                                      title: { display: true, text: 'Weight %', font: { size: 11 } },
                                    },
                                  },
                                }}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="chart-loading">Loading tilts data...</div>
                    )}
                  </div>
                )}

                {/* Holdings History Tab */}
                {activeTab === 'holdings-history' && (
                  <div className="tab-panel">
                    <div className="chart-controls">
                      <div className="chart-portfolio-selector">
                        <label htmlFor="holdings-history-portfolio">Select Portfolio:</label>
                        <select
                          id="holdings-history-portfolio"
                          value={holdingsHistoryPortfolio}
                          onChange={(e) => setHoldingsHistoryPortfolio(e.target.value)}
                        >
                          {selectedPortfolios.map(code => {
                            const portfolio = portfolios.find(p => p.portfolio_code === code);
                            return (
                              <option key={code} value={code}>
                                {portfolio?.display_name || portfolio?.portfolio_name || code}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                    </div>
                    {holdingsHistoryData ? (
                      <div className="holdings-history-container">
                        <div className="table-container holdings-history-table-container">
                          <table className="holdings-history-table">
                            <thead>
                              <tr>
                                <th className="name-col sticky-col">Asset Class / Holding</th>
                                {holdingsHistoryData.months.map(month => (
                                  <React.Fragment key={month.label}>
                                    {expandedMonths[month.label] ? (
                                      /* Expanded: show individual date headers */
                                      month.dates.map((date, idx) => {
                                        const d = new Date(date);
                                        const formatted = d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: '2-digit' });
                                        return (
                                          <th
                                            key={date}
                                            className={idx === 0 ? 'month-header expanded' : 'date-header'}
                                            onClick={idx === 0 ? () => toggleMonthExpansion(month.label) : undefined}
                                          >
                                            {idx === 0 && <span className="expand-icon">▼</span>}
                                            {formatted}
                                          </th>
                                        );
                                      })
                                    ) : (
                                      /* Collapsed: show month summary with latest date */
                                      (() => {
                                        const d = new Date(month.dates[0]);
                                        const formatted = d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: '2-digit' });
                                        return (
                                          <th
                                            className="month-header"
                                            onClick={() => toggleMonthExpansion(month.label)}
                                          >
                                            <span className="expand-icon">▶</span>
                                            {formatted}
                                            {month.dates.length > 1 && <span className="date-count">(+{month.dates.length - 1})</span>}
                                          </th>
                                        );
                                      })()
                                    )}
                                  </React.Fragment>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {holdingsHistoryData.asset_classes.map((assetClass, acIndex) => (
                                <React.Fragment key={assetClass.name}>
                                  {/* Asset Class Row (expandable) */}
                                  <tr
                                    className={`asset-class-row ${expandedAssetClasses[assetClass.name] ? 'expanded' : ''}`}
                                    onClick={() => toggleAssetClassExpansion(assetClass.name)}
                                  >
                                    <td className="name-col sticky-col">
                                      <span className="expand-icon">{expandedAssetClasses[assetClass.name] ? '▼' : '▶'}</span>
                                      <span
                                        className="asset-class-indicator"
                                        style={{ backgroundColor: getAssetClassColor(assetClass.name, acIndex) }}
                                      />
                                      {assetClass.name}
                                    </td>
                                    {holdingsHistoryData.months.map(month => (
                                      <React.Fragment key={month.label}>
                                        {expandedMonths[month.label] ? (
                                          month.dates.map(date => (
                                            <td key={date} className="value-col">
                                              {assetClass.totals[date] != null ? `${assetClass.totals[date].toFixed(1)}%` : '-'}
                                            </td>
                                          ))
                                        ) : (
                                          <td className="value-col">
                                            {assetClass.totals[month.dates[0]] != null ? `${assetClass.totals[month.dates[0]].toFixed(1)}%` : '-'}
                                          </td>
                                        )}
                                      </React.Fragment>
                                    ))}
                                  </tr>
                                  {/* Individual Holdings (shown when expanded) */}
                                  {expandedAssetClasses[assetClass.name] && assetClass.holdings.map(holding => (
                                    <tr key={holding.asset_name} className="holding-row">
                                      <td className="name-col sticky-col holding-name">
                                        <span className="holding-indent">└</span>
                                        {holding.asset_name}
                                        {holding.apir_code && <span className="apir-code">{holding.apir_code}</span>}
                                      </td>
                                      {holdingsHistoryData.months.map(month => (
                                        <React.Fragment key={month.label}>
                                          {expandedMonths[month.label] ? (
                                            month.dates.map(date => (
                                              <td key={date} className="value-col holding-value">
                                                {holding.values[date] != null ? `${holding.values[date].toFixed(1)}%` : '-'}
                                              </td>
                                            ))
                                          ) : (
                                            <td className="value-col holding-value">
                                              {holding.values[month.dates[0]] != null ? `${holding.values[month.dates[0]].toFixed(1)}%` : '-'}
                                            </td>
                                          )}
                                        </React.Fragment>
                                      ))}
                                    </tr>
                                  ))}
                                </React.Fragment>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ) : (
                      <div className="chart-loading">Loading holdings history...</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {selectedPortfolios.length === 0 && portfolios.length > 0 && (
        <div className="no-selection">
          <p>Select one or more portfolios to view their holdings and allocations.</p>
        </div>
      )}

      {portfolios.length === 0 && (
        <div className="no-portfolios">
          <p>No portfolios available. Please contact your administrator.</p>
        </div>
      )}
      {/* Research Report Modals */}
      {openReportModals.map(apir => (
        <ResearchReportModal
          key={apir}
          isOpen={true}
          onClose={() => setOpenReportModals(prev => prev.filter(a => a !== apir))}
          apirCode={apir}
        />
      ))}
    </div>
  );
};

export default PortfolioViewerApp;
